import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { PenLine, X, Send, Loader2, AlertCircle } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckoutDatesEditor } from "@/components/checkout/CheckoutDatesEditor";
import { CheckoutLocationEditor } from "@/components/checkout/CheckoutLocationEditor";
import { api, getDistanceUnitPlural, type DeliveryAirport, type AvailabilityData } from "@/lib/api";
import { bookingsApi, QuoteApiError, type PriceQuote, type TripAdjustmentQuote, type TripAdjustment } from "@/lib/bookings-api";
import { composeAdjIso, prefillAdjTime, hhmmTo12h, time12ToHhmm, getTripPickupIso } from "@/lib/trip-datetime";
import { formatMoneyWithCurrency } from "@/lib/formatMoney";
import { currencySymbolPadding } from "@/lib/utils";
import { getCurrencySymbol } from "@workspace/api-client-react";
import { getCommittedTripTotal, getCommittedOwnerEarn } from "@/lib/committed-total";
import { knownMoney, hasRequestFinancialSnapshot, type RequestFinancialSnapshot } from "@/lib/request-financial-baseline";
import { computeRenterAdjustmentDelta } from "@/lib/adjustment-delta";
import { getRequestChangeLocationPolicy, hasPersistedTripDelivery } from "@/lib/request-change-location-policy";
// Task #1676: shared location-summary + reverse-geocode prefill helpers. They
// live in @/lib and @/hooks (a single source of truth that the reservation
// panel and in-thread proposal cards also consume) — imported here directly
// rather than via the ConversationPage page module so this shared component
// never value-imports a page (that tangle is the chunk-cycle TDZ white-screen).
import { getLocationSummary } from "@/lib/location-summary";
import { useReverseGeocodePrefill } from "@/hooks/useReverseGeocodePrefill";
import { useLocalDraft } from "@/hooks/useLocalDraft";
import { buildPriceBreakdownLines, hostPriceTotal, snapHoursToHalf, type PriceLine } from "@workspace/booking-rules";

/**
 * Subset of TripData / ConversationDetail.active_trip the modal needs to
 * seed itself. Both of those wider shapes satisfy this interface, so the
 * three call sites (ConversationPage, TripDetailPage, OwnerBookingDetailPage)
 * can pass through their existing trip object without remapping.
 */
export interface RequestChangeTrip {
  appliedDiscount?: {
    pro_owner_discount?: number | null; pro_owner_tier?: string | null;
    coupon_amount?: number | null; coupon_code?: string | null;
  } | null;
  bookingClock?: { started: boolean; inProgress: boolean; pickup: string | null; return: string | null };
  id: number;
  isOwner?: boolean | null;
  /** Task #2583 — when `in_progress`/`active` only end date/time is editable. */
  status?: string | null;
  pick_up?: string | null;
  started_at?: string | null;
  drop_off?: string | null;
  rental_fee?: number | null;
  // Task #2610 — Original column is sourced from the booking's stored
  // fees (the last-agreed state), not a fresh re-quote. These fields
  // are populated by `conv.active_trip` / `TripData` already.
  service_fee?: number | null;
  insurance_fee?: number | null;
  delivery_fee?: number | null;
  payment_due?: number | null;
  // Task #2641 — host payout snapshot. Surfaced as the "Your earnings"
  // row on the Original column so the host can compare current vs
  // proposed take-home at a glance.
  owner_earn?: number | null;
  insurance_package_name?: string | null;
  host_insurance_enabled?: boolean | null;
  host_insurance_fee?: number | null;
  host_insurance_policy_snapshot?: TripAdjustment["host_insurance_policy_snapshot"];
  times_captured?: boolean | null;
  is_delivery?: boolean | null;
  delivery_distance?: number | null;
  airport_code?: string | null;
  delivery_address?: string | null;
  pickup_latitude?: number | null;
  pickup_longitude?: number | null;
  rental_type?: string | null;
  billing_currency_code?: string | null;
  // Task #3531 — partial-pay bookings move only the platform portion on
  // the renter's card; the owner-direct slice is paid at pickup. Used to
  // split the renter-facing "+X vs original" preview into card vs pickup.
  payment_type?: string | null;
  owner_earn_before_host_insurance?: number | null;
}

/**
 * Task #2610 — when a change proposal is pending acceptance, the
 * "Original" column should reflect the most recently *suggested* fees
 * (not the last-agreed booking total), so both sides iterate against
 * the latest offer. Callers pass the latest pending TripAdjustment row
 * here; omit it (or pass null) for booked-but-no-pending-proposal
 * trips, in which case the column reads from the trip's stored fees.
 */
export interface RequestChangePendingProposal {
  id: number;
  rental_fee: number;
  service_fee: number;
  insurance_fee: number;
  delivery_fee: number;
  payment_due: number;
  // Task #2641 — when a counter-proposal is the baseline shown in the
  // Original column, surface the suggested host payout there too so
  // the "Your earnings" line stays consistent with which baseline is
  // being countered.
  owner_earn?: number | null;
  insurance_package_name?: string | null;
  // Task #2692 — when countering a pending proposal, the Original
  // column must also reflect the proposal's dates / times / location
  // (not the last-agreed trip values). These nullable fields mirror
  // the `trip_adjustments` row shape and fall back to the trip's
  // stored values when omitted (legacy rows).
  pickup_at?: string | null;
  dropoff_at?: string | null;
  is_delivery?: boolean | null;
  delivery_distance?: number | null;
  airport_code?: string | null;
  delivery_address?: string | null;
  pickup_latitude?: number | null;
  pickup_longitude?: number | null;
}

export interface RequestChangeCar {
  id?: number | null;
  slug?: string | null;
  city?: string | null;
  state?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  country_code?: string | null;
}

/**
 * Optional override for the modal's initial form values, used by the
 * counter-proposal flow to seed from the proposal being countered instead
 * of the current trip terms. Any field left undefined falls back to the
 * trip-derived value.
 */
export interface RequestChangeSeed {
  dateFrom?: string;
  dateTo?: string;
  rentalFee?: string;
  deliveryFee?: string;
  pickupAt?: string | null;
  dropoffAt?: string | null;
  isDelivery?: boolean | null;
  deliveryDistance?: number | null;
  airportCode?: string | null;
  deliveryAddress?: string | null;
  // Task #2692 — coords for the proposed pickup location so countering
  // a guest's delivery request preserves their lat/lng (re-quote uses
  // distance/airport, but submit needs to round-trip coords).
  pickupLatitude?: number | null;
  pickupLongitude?: number | null;
  // Task #2692 — which fee inputs were seeded by the *other party*'s
  // proposal (vs. derived from the trip's last-agreed values). The
  // counter modal uses this to label seeded fee rows as
  // "GUEST-PROPOSED" (or "OWNER-PROPOSED" when the renter is
  // countering a host) until the viewer actually edits them.
  rentalFeeSource?: "guest" | "owner" | null;
  deliveryFeeSource?: "guest" | "owner" | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  trip: RequestChangeTrip | null | undefined;
  car: RequestChangeCar | null | undefined;
  token: string | null | undefined;
  /**
   * When set, the new suggestion is recorded as a counter-proposal to the
   * given adjustment id (the API marks the parent superseded). Cleared on
   * close so a fresh open starts as a brand-new proposal.
   */
  counterOfId?: number | null;
  /** Counter-flow seed values that win over the trip-derived defaults. */
  seed?: RequestChangeSeed;
  /**
   * Task #2610 — when a proposal is pending, the Original column shows
   * the last *suggested* values from this row instead of the booking's
   * stored last-agreed fees. The badge also flips from "Current trip"
   * to "Last proposal" so the host knows what they're countering.
   */
  pendingProposal?: RequestChangePendingProposal | null;
  /**
   * Fired after the server accepted the new TripAdjustment. The caller is
   * responsible for refreshing whatever views surface this trip (negotiation
   * history, dashboard counters, conversation messages, etc.) — the modal
   * stays focused on the create-and-close flow.
   */
  onSuccess?: (adjustment: TripAdjustment) => void | Promise<void>;
}

/**
 * Task #2379: minimum allowed rental fee for an owner-overridable
 * proposal. Returns the floor amount, the multiplier basis used to
 * derive it (e.g. "× 3 days" / "× 6 hours"), and whether the floor
 * data was actually available. When `available` is false, owners with
 * a touched rental-fee override are blocked from submitting because
 * we have no authoritative minimum to enforce — degrading to "no
 * floor" would let owners bypass the per-car minimum, so we hard-fail
 * instead. Numbers are rounded to whole pennies.
 *  - self-drive (`driving`): minDailySelfDrive × numDays
 *  - event:                   minDailyEvent     × numDays
 *  - chauffeured:             minHourlyChauffeured × billable hours
 *
 * Per-currency floor is already baked into `minHourlyChauffeured` by
 * `chauffeuredHourlyFloorFor()` server-side.
 */
export interface RentalFeeFloor {
  amount: number;
  multiplier: number;
  unit: "day" | "hour";
  available: boolean;
}

/**
 * Task #4599 — derive the billable chauffeured hours from the modal's
 * "HH:MM" (24h) pickup / drop-off strings, snapped the same way the
 * BookingWidget snaps them (`snapHoursToHalf(span, 1)`). Returns
 * `undefined` when either side is empty / malformed so the caller leaves
 * the `hours` quote param off entirely (the server then keeps its own
 * default — we never invent an hour count for a genuinely time-less
 * chauffeured quote). Same-day only: the modal locks chauffeured trips to
 * drop-off === pick-up day, so a single-day span is the contract here.
 */
export function deriveChauffeuredHours(pickupHhmm: string, dropoffHhmm: string): number | undefined {
  const toDecimal = (hhmm: string): number | null => {
    const [h, m] = hhmm.split(":").map((s) => Number(s));
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    return h + m / 60;
  };
  const ph = toDecimal(pickupHhmm);
  const dh = toDecimal(dropoffHhmm);
  if (ph == null || dh == null) return undefined;
  const span = dh - ph;
  if (!(span > 0)) return undefined;
  return snapHoursToHalf(span, 1);
}
/**
 * Compute the minimum rental fee floor for a change request.
 *
 * When `originalRentalFee` is supplied (the trip's stored `rental_fee` at the
 * time the modal opens), the effective floor is capped at that value:
 *   effectiveFloor = Math.min(carFloor, originalRentalFee)
 *
 * This means a host who proposes an amount ≥ the original booking fee is
 * never blocked by a car-floor that rose after the original booking was made.
 * The cap only applies when the floor is `available`; the unavailable hard-fail
 * path (no floor data from the server) is unchanged.
 */
export function computeMinRentalFee(
  quote: Pick<PriceQuote, "numDays" | "rentalType"> &
    Partial<Pick<PriceQuote, "pricingFloors" | "hoursRequested" | "eventHoursRequested">> | null,
  originalRentalFee?: number | null,
): RentalFeeFloor {
  if (!quote) {
    return { amount: 0, multiplier: 0, unit: "day", available: false };
  }
  const days = Math.max(1, Number(quote.numDays) || 1);
  const floors = quote.pricingFloors;
  if (!floors) {
    // No authoritative floor data on this quote response. Surface the
    // unit + multiplier so the UI can still explain the basis, and
    // mark the floor unavailable so submit blocks for owner overrides.
    if (quote.rentalType === "chauffeured") {
      const hoursPerDay = Number(quote.hoursRequested ?? quote.eventHoursRequested) || 0;
      const totalHours = Math.max(1, hoursPerDay * days);
      return { amount: 0, multiplier: totalHours, unit: "hour", available: false };
    }
    return { amount: 0, multiplier: days, unit: "day", available: false };
  }
  // Cap the car's current floor at the original booking fee when provided.
  // A host raising (or holding steady at) an amount already agreed at booking
  // time should never be blocked by a floor that shifted after the fact.
  const capAmount = (raw: number): number => {
    if (originalRentalFee != null && Number.isFinite(originalRentalFee) && originalRentalFee > 0) {
      return Math.min(raw, originalRentalFee);
    }
    return raw;
  };
  if (quote.rentalType === "event") {
    return {
      amount: capAmount(Math.round(floors.minDailyEvent * days * 100) / 100),
      multiplier: days,
      unit: "day",
      available: true,
    };
  }
  if (quote.rentalType === "chauffeured") {
    const hoursPerDay = Number(quote.hoursRequested ?? quote.eventHoursRequested) || 0;
    const totalHours = Math.max(1, hoursPerDay * days);
    return {
      amount: capAmount(Math.round(floors.minHourlyChauffeured * totalHours * 100) / 100),
      multiplier: totalHours,
      unit: "hour",
      available: true,
    };
  }
  return {
    amount: capAmount(Math.round(floors.minDailySelfDrive * days * 100) / 100),
    multiplier: days,
    unit: "day",
    available: true,
  };
}

function formatShortDate(iso: string): string {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function buildDateTimeSummary(dateFrom: string, dateTo: string, pickup12: string, dropoff12: string, t: (k: string, fb?: string) => string): string {
  if (!dateFrom || !dateTo) return t("conversation.dates_pick_to_continue", "Pick dates");
  const haveTimes = !!pickup12 && !!dropoff12;
  if (!haveTimes) return `${formatShortDate(dateFrom)} – ${formatShortDate(dateTo)}`;
  if (dateFrom === dateTo) return `${formatShortDate(dateFrom)}, ${pickup12} – ${dropoff12}`;
  return `${formatShortDate(dateFrom)} ${pickup12} – ${formatShortDate(dateTo)} ${dropoff12}`;
}

/**
 * Side-by-side row used by both columns. Renders a label + value, with
 * an optional "Change" trigger on the proposed side.
 */
function ColumnSection({
  title,
  primary,
  secondary,
  trigger,
}: {
  title: string;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  trigger?: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-[#5a6b5a]">{title}</p>
          <div className="mt-1 break-words text-sm text-brand">{primary}</div>
          {secondary && <div className="mt-0.5 text-[11px] text-[#8a9a8a]">{secondary}</div>}
        </div>
        {trigger && <div className="shrink-0">{trigger}</div>}
      </div>
    </div>
  );
}

/**
 * Build itemised price lines + total from a quote. We intentionally do NOT
 * include climate offset or refundable-cancellation surcharge here: those
 * toggles live on the renter's checkout step and aren't part of a host/renter
 * adjustment proposal.
 */
function summariseQuote(quote: RequestFinancialSnapshot | null, host = false): { total: number; breakdown: PriceLine[] } | null {
  if (!quote) return null;
  const total = host ? hostPriceTotal(quote) : quote.totalIncludingTax ?? quote.paymentDue;
  if (total == null) return null;
  const breakdown = buildPriceBreakdownLines({ ...quote, tax: host ? undefined : quote.tax, numDays: quote.numDays ?? 1 }, {
    couponDiscount: host ? 0 : Number(quote.couponDiscount || 0),
    couponCode: host ? null : quote.appliedCoupon?.code ?? null,
    proOwnerDiscount: host ? 0 : quote.proOwnerBookingDiscount,
    proOwnerTier: host ? null : quote.proOwnerTier,
    canonicalTotal: total,
  });
  return { total, breakdown };
}

/**
 * Shared "Request a change" / "Counter proposal" modal extracted from
 * ConversationPage so TripDetailPage and OwnerBookingDetailPage can
 * offer the same UI (and post the same payload) without re-implementing
 * the dates editor + location editor + owner fee fields.
 *
 * Task #2379 redesign: side-by-side Original vs Proposed comparison
 * with a live recalculated price (debounced refetch). Both columns
 * surface trip dates & times, pick-up & drop-off, and a price panel
 * with the same breakdown rows the checkout rail uses (rental,
 * service, insurance, delivery, tax, coupon) plus the rolled-up
 * total. The Proposed column re-uses `buildPriceBreakdownLines` so
 * the math reconciles to `totalIncludingTax`. Owner-only: rental_fee
 * and delivery_fee inputs override the auto-computed quote, with the
 * rental fee floored at the per-car suggested-pricing minimum
 * (`computeMinRentalFee`). Send Proposal is disabled while the quote
 * is loading, when the quote errored, or when the floor is breached.
 * The created TripAdjustment payload is unchanged — no server contract
 * change there.
 */
/**
 * Task #2655 — State model spec for the host-side Build Your Quote
 * pipeline. The bugs this task fixes (cross-fee clobber on first
 * edit, misleading "Calculated" hint, time-edit recalc, TimePicker
 * flicker) all stem from the rules below being implicit. Document them
 * here so the next change doesn't have to re-derive them from the
 * effect graph.
 *
 * Inputs that can shape the proposed quote:
 *   • dateFrom / dateTo                                — refetch quote
 *   • pickupTime / dropoffTime                         — refetch quote
 *   • isDelivery / deliveryDistance / airportCode      — refetch quote
 *   • deliveryAddress                                  — proposal only
 *     (does not refetch — only address text differs)
 *   • rentalFee (host only)                            — override
 *   • deliveryFee (host only)                          — override
 *
 * Derived values and their refresh rules:
 *   • `quote`                — refetched (debounced) whenever a
 *     refetch input changes, AND whenever the host edits a fee
 *     input (the typed value is forwarded as
 *     `rental_fee_override` / `delivery_fee_override` so the server
 *     returns canonical `serviceFee` / `ownerEarn` / `paymentDue`).
 *   • `originalSummary`      — synthesized from the trip's stored fees
 *     (or the pending proposal). Never re-quoted; this is the
 *     "last-agreed / last-suggested" anchor.
 *   • `proposedSummary`      — `buildPriceBreakdownLines(quote)`. The
 *     server quote is already the source of truth — no client-side
 *     rate back-derivation (Task #2959).
 *   • `effectiveProposedSummary` — mirrors `originalSummary` until
 *     `proposalEdited` flips, then switches to `proposedSummary`.
 *   • rental-fee hint baseline — `rentalFeeHintOriginal` (from the
 *     Original column's reconciled rental row) when no edit has
 *     happened; `quote.rentalFee` after the first edit.
 *   • Fee-input auto-fill    — runs only when the underlying
 *     `quote.rentalFee` / `quote.deliveryFee` *number* changes from
 *     the prior quote AND the corresponding input hasn't been touched.
 *     Because a fee edit re-quotes with that same value as an
 *     override, the server returns it back verbatim — so the sibling
 *     input still isn't auto-overwritten as a side effect of typing.
 */
export function RequestChangeModal({ open, onClose, trip, car, token, counterOfId, seed, pendingProposal, onSuccess }: Props) {
  const { t } = useTranslation();
  const isOwner = !!trip?.isOwner;
  const tripRentalType = (trip?.rental_type as "driving" | "chauffeured" | "event" | undefined) || "driving";
  const currency = trip?.billing_currency_code || "USD";
  const sym = getCurrencySymbol(currency);

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Chauffeured trips are single-day: keep the proposed drop-off date locked
  // to the pick-up date so a change request can't turn a chauffeured booking
  // into a cross-midnight one. The dates editor enforces this on edit; this
  // also normalises a legacy multi-day trip opened for a change request.
  useEffect(() => {
    if (tripRentalType === "chauffeured" && dateFrom && dateTo && dateTo !== dateFrom) {
      setDateTo(dateFrom);
    }
  }, [tripRentalType, dateFrom, dateTo]);
  const [rentalFee, setRentalFee] = useState("");
  const [deliveryFee, setDeliveryFee] = useState("");
  const [message, setMessage] = useState("");
  const [pickupTime, setPickupTime] = useState("");
  const [dropoffTime, setDropoffTime] = useState("");
  const [isDelivery, setIsDelivery] = useState(false);
  const [deliveryDistance, setDeliveryDistance] = useState<number | null>(null);
  const [airportCode, setAirportCode] = useState<string | null>(null);
  const [deliveryAddress, setDeliveryAddress] = useState<string | null>(null);
  const [pickupLat, setPickupLat] = useState<number | null>(null);
  const [pickupLng, setPickupLng] = useState<number | null>(null);
  const [carDeliveryAirports, setCarDeliveryAirports] = useState<DeliveryAirport[]>([]);
  const [carDeliveryEnabled, setCarDeliveryEnabled] = useState(false);
  const [selfDriveDeliveryEnabled, setSelfDriveDeliveryEnabled] = useState(false);
  const [carDeliveryRadius, setCarDeliveryRadius] = useState<number | null>(null);
  const [deliveryOptionsStatus, setDeliveryOptionsStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const deliveryOptionsRequestRef = useRef(0);
  const [availability, setAvailability] = useState<AvailabilityData | null>(null);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Task #2610 — the Original column is sourced from the booking record
  // (or, when a proposal is pending, from that proposal) rather than a
  // fresh `bookingsApi.getQuote` against the car's current rates. The
  // previous re-quote approach silently drifted whenever the host had
  // edited the car's price_driving since the booking was made, and it
  // also failed to reflect the latest pending counter-proposal during a
  // negotiation. See `originalSummary` below for the construction.

  // Live recalculated proposed quote.
  const [quote, setQuote] = useState<TripAdjustmentQuote | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  // Task #2910 — capture the full typed error (code + maxRadius +
  // message) so the Price section can branch on `code` the same way
  // CheckoutPage / BookingWidget already do for
  // DELIVERY_BEYOND_MAX_RADIUS, and fall back to the server's
  // human-readable `error` string for every other documented case
  // instead of the old hardcoded "adjust the dates" copy.
  const [quoteError, setQuoteError] = useState<{ message: string; code?: string; maxRadius?: number } | null>(null);
  const [rentalFeeTouched, setRentalFeeTouched] = useState(false);
  const [deliveryFeeTouched, setDeliveryFeeTouched] = useState(false);
  // Task #2692 — distinct from `*Touched` (which gates auto-overwrite
  // from the live quote): these flip true ONLY when the viewer
  // actually types into the fee input in this counter. Used by the
  // fee-row badge so a seeded guest fee renders as "GUEST-PROPOSED"
  // until the host edits it, then flips to "OWNER-PROPOSED".
  const [rentalFeeOwnerEdited, setRentalFeeOwnerEdited] = useState(false);
  const [deliveryFeeOwnerEdited, setDeliveryFeeOwnerEdited] = useState(false);
  const [rentalFeeSeedSource, setRentalFeeSeedSource] = useState<"guest" | "owner" | null>(null);
  const [deliveryFeeSeedSource, setDeliveryFeeSeedSource] = useState<"guest" | "owner" | null>(null);
  // Task #2641 — flips true the moment the host edits anything that
  // legitimately changes the proposal (dates, times, pickup/delivery
  // location, or the fee inputs themselves). Until then the Proposed
  // column mirrors the Original — no flicker / no auto-overwrite of
  // the rental fee from the live quote on open. When a counter-proposal
  // `seed` is provided, the seeded values are already the host's
  // intended starting point, so we initialise to `true` and let the
  // live quote flow as before.
  const [proposalEdited, setProposalEdited] = useState(false);
  const [baselineSnapshot, setBaselineSnapshot] = useState<RequestFinancialSnapshot | null>(null);
  const [baselineLoading, setBaselineLoading] = useState(false);
  const quoteTermsKey = JSON.stringify([
    trip?.id, dateFrom, dateTo, pickupTime, dropoffTime, isDelivery,
    deliveryDistance, airportCode, deliveryAddress, pickupLat, pickupLng,
    rentalFeeTouched ? rentalFee : null, deliveryFeeTouched ? deliveryFee : null,
  ]);
  const [quotedTermsKey, setQuotedTermsKey] = useState<string | null>(null);

  // Track car for availability fetch and race-protection refs for the
  // proposed-quote pipeline.
  const availabilityCarIdRef = useRef<number | string | null>(null);
  const availabilityAttemptedRef = useRef<number | string | null>(null);
  const quoteVersionRef = useRef(0);

  // Task #7654 — persist in-progress edits so an accidental close doesn't
  // lose them. ONLY for the plain change-request flow: counter flows
  // (`seed` / `counterOfId`) carry the other party's proposal as the
  // starting point and must never be clobbered by a stale local draft.
  const isCounterFlow = !!seed || counterOfId != null;
  const draftKey =
    trip?.id != null && !isCounterFlow ? `change-request:${trip.id}` : null;
  // Trip-derived prefill captured at open. Stored INSIDE the draft so a
  // later restore can tell "user edited this" (differs from the baseline it
  // was saved against) from "this is just the stale prefill of an older
  // trip state" — the latter must never clobber fresher server values.
  type ChangeDraftFields = {
    message: string; dateFrom: string; dateTo: string;
    rentalFee: string; deliveryFee: string; pickupTime: string; dropoffTime: string;
  };
  const draftBaselineRef = useRef<Omit<ChangeDraftFields, "message"> | null>(null);
  const draftValues = useMemo(
    () => ({
      message, dateFrom, dateTo, rentalFee, deliveryFee, pickupTime, dropoffTime,
      baseline: draftBaselineRef.current,
    }),
    [message, dateFrom, dateTo, rentalFee, deliveryFee, pickupTime, dropoffTime],
  );
  const { readDraft: readChangeDraft, clearDraft: clearChangeDraft } = useLocalDraft({
    key: draftKey,
    version: 2,
    values: draftValues,
    enabled: open,
    // Nothing meaningfully typed: empty message and every field still equal
    // to the open-time prefill baseline.
    isEmpty: (v) =>
      !v.message &&
      (v.baseline == null ||
        (v.dateFrom === v.baseline.dateFrom &&
          v.dateTo === v.baseline.dateTo &&
          v.rentalFee === v.baseline.rentalFee &&
          v.deliveryFee === v.baseline.deliveryFee &&
          v.pickupTime === v.baseline.pickupTime &&
          v.dropoffTime === v.baseline.dropoffTime)),
  });

  useEffect(() => {
    if (!open || !trip) return;
    const pickUp = getTripPickupIso(trip);
    const tripDateFrom = pickUp ? pickUp.split("T")[0] : "";
    const tripDateTo = trip.drop_off ? trip.drop_off.split("T")[0] : "";
    const tripRentalFee = trip.rental_fee != null ? String(Number(trip.rental_fee).toFixed(2)) : "";
    const tripDeliveryFee = trip.delivery_fee != null && trip.delivery_fee > 0 ? String(Number(trip.delivery_fee).toFixed(2)) : "";
    setDateFrom(seed?.dateFrom ?? tripDateFrom);
    setDateTo(seed?.dateTo ?? tripDateTo);
    setRentalFee(seed?.rentalFee ?? tripRentalFee);
    setDeliveryFee(seed?.deliveryFee ?? tripDeliveryFee);
    setMessage("");
    setError("");
    setQuote(null);
    setQuoteError(null);
    setRentalFeeTouched(seed?.rentalFee !== undefined);
    setDeliveryFeeTouched(seed?.deliveryFee !== undefined);
    // Task #2692 — the viewer hasn't actually edited anything yet
    // when a counter is opened from a guest/host proposal; the seed
    // came from the other party. These flip true only on direct user
    // input via the fee onChange handlers below.
    setRentalFeeOwnerEdited(false);
    setDeliveryFeeOwnerEdited(false);
    setRentalFeeSeedSource(seed?.rentalFeeSource ?? null);
    setDeliveryFeeSeedSource(seed?.deliveryFeeSource ?? null);
    // Task #2737 — on Counter open the Proposed column must mirror
    // the persisted proposal row verbatim (rental, delivery, service,
    // payment_due, owner_earn from `pendingProposal`) so the modal
    // matches the inline proposal card to the penny. Flipping
    // `proposalEdited` true on seed (previous behaviour, task #2716)
    // forced the column through the client-side override re-derivation
    // (now removed in Task #2959 in favour of a server re-quote), which
    // fell back to a 0.80 ownerShare guess and drifted whenever the
    // booking's real host share was anything else (~0.766 in the bug
    // report → £16 overstatement). Keep it false on
    // open; the existing edit handlers (date, time, location, fee
    // inputs) still flip it true on the first real edit, at which
    // point the live re-quote takes over.
    setProposalEdited(false);
    const pickupSource = seed?.pickupAt !== undefined ? seed.pickupAt : pickUp;
    const dropoffSource = seed?.dropoffAt !== undefined ? seed.dropoffAt : trip.drop_off;
    const seedTimesCaptured = seed?.pickupAt !== undefined ? true : trip.times_captured;
    setPickupTime(prefillAdjTime(pickupSource, seedTimesCaptured));
    setDropoffTime(prefillAdjTime(dropoffSource, seedTimesCaptured));
    const tripIsDelivery = hasPersistedTripDelivery(trip);
    const seededIsDelivery = seed?.isDelivery ?? tripIsDelivery;
    setIsDelivery(seededIsDelivery);
    const distanceSource = seed?.isDelivery !== undefined ? seed.deliveryDistance ?? null : (tripIsDelivery ? trip.delivery_distance ?? null : null);
    const airportSource = seed?.isDelivery !== undefined ? seed.airportCode ?? null : (tripIsDelivery ? trip.airport_code ?? null : null);
    const addressSource = seed?.isDelivery !== undefined ? seed.deliveryAddress ?? null : (tripIsDelivery ? trip.delivery_address ?? null : null);
    setDeliveryDistance(seededIsDelivery ? distanceSource : null);
    setAirportCode(seededIsDelivery ? airportSource : null);
    setDeliveryAddress(seededIsDelivery ? addressSource : null);
    setPickupLat(seededIsDelivery
      ? (seed?.pickupLatitude !== undefined ? seed.pickupLatitude : trip.pickup_latitude ?? null)
      : null);
    setPickupLng(seededIsDelivery
      ? (seed?.pickupLongitude !== undefined ? seed.pickupLongitude : trip.pickup_longitude ?? null)
      : null);

    // Task #7654 — restore a saved draft on top of the trip-derived seed.
    // Counter flows are excluded above (draftKey is null then), so a stale
    // local draft can never clobber the other party's proposal. Restored
    // fields flip the same touched/edited flags typing would, so the live
    // re-quote pipeline and fee auto-fill behave exactly as if the user had
    // re-entered the values by hand.
    const openPickupTime = prefillAdjTime(pickupSource, seedTimesCaptured);
    const openDropoffTime = prefillAdjTime(dropoffSource, seedTimesCaptured);
    draftBaselineRef.current = {
      dateFrom: tripDateFrom,
      dateTo: tripDateTo,
      rentalFee: tripRentalFee,
      deliveryFee: tripDeliveryFee,
      pickupTime: openPickupTime,
      dropoffTime: openDropoffTime,
    };
    if (!isCounterFlow) {
      const draft = readChangeDraft();
      if (draft) {
        // Apply a drafted field only when the user genuinely edited it in
        // the session that saved it (differs from that session's baseline)
        // AND it still differs from the current trip-derived prefill. A
        // stale untouched prefill from an older trip state is skipped.
        const userEdited = (field: keyof NonNullable<typeof draft.baseline>) =>
          draft.baseline == null || draft[field] !== draft.baseline[field];
        let edited = false;
        if (typeof draft.message === "string" && draft.message) {
          setMessage(draft.message);
        }
        if (typeof draft.dateFrom === "string" && draft.dateFrom && draft.dateFrom !== tripDateFrom && userEdited("dateFrom")) {
          setDateFrom(draft.dateFrom);
          edited = true;
        }
        if (typeof draft.dateTo === "string" && draft.dateTo && draft.dateTo !== tripDateTo && userEdited("dateTo")) {
          setDateTo(draft.dateTo);
          edited = true;
        }
        if (isOwner && typeof draft.rentalFee === "string" && draft.rentalFee && draft.rentalFee !== tripRentalFee && userEdited("rentalFee")) {
          setRentalFee(draft.rentalFee);
          setRentalFeeTouched(true);
          setRentalFeeOwnerEdited(true);
          edited = true;
        }
        if (isOwner && typeof draft.deliveryFee === "string" && draft.deliveryFee && draft.deliveryFee !== tripDeliveryFee && userEdited("deliveryFee")) {
          setDeliveryFee(draft.deliveryFee);
          setDeliveryFeeTouched(true);
          setDeliveryFeeOwnerEdited(true);
          edited = true;
        }
        if (typeof draft.pickupTime === "string" && draft.pickupTime && draft.pickupTime !== openPickupTime && userEdited("pickupTime")) {
          setPickupTime(draft.pickupTime);
          edited = true;
        }
        if (typeof draft.dropoffTime === "string" && draft.dropoffTime && draft.dropoffTime !== openDropoffTime && userEdited("dropoffTime")) {
          setDropoffTime(draft.dropoffTime);
          edited = true;
        }
        if (edited) setProposalEdited(true);
      }
    }
    // readChangeDraft is identity-stable in behaviour (reads latest via ref);
    // adding it to deps would re-run the seed effect on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, trip, seed]);

  // Booking-bound capability lookup also works when the booked listing is no
  // longer active. Do not infer the explicit self-drive flag from the trip's
  // current delivery mode; on a failed lookup preserve the existing delivery
  // draft but show a retryable unavailable state for new delivery choices.
  const fetchBookedCarDeliveryOptions = () => {
    const requestId = ++deliveryOptionsRequestRef.current;
    setDeliveryOptionsStatus("loading");
    setCarDeliveryAirports([]);
    setCarDeliveryEnabled(false);
    setSelfDriveDeliveryEnabled(false);
    setCarDeliveryRadius(null);
    const lookup = bookingsApi.getBookedCarDeliveryOptions;
    if (typeof lookup !== "function") {
      setDeliveryOptionsStatus("error");
      return;
    }
    void lookup.call(bookingsApi, token, trip!.id).then((options) => {
      if (deliveryOptionsRequestRef.current !== requestId) return;
      setCarDeliveryAirports(options.deliveryAirports ?? []);
      setCarDeliveryEnabled(options.delivery_enabled === true);
      const selfDriveDelivery = options.self_drive_delivery_enabled === true;
      setSelfDriveDeliveryEnabled(selfDriveDelivery);
      const tripAlreadyDelivery = hasPersistedTripDelivery(trip);
      if (tripRentalType === "driving" && !selfDriveDelivery && !tripAlreadyDelivery) {
        setIsDelivery(false);
        setDeliveryDistance(null);
        setAirportCode(null);
        setDeliveryAddress(null);
        setPickupLat(null);
        setPickupLng(null);
      }
      setCarDeliveryRadius(
        typeof options.delivery_maximum_distance === "number"
          ? options.delivery_maximum_distance
          : null,
      );
      setDeliveryOptionsStatus("ready");
    }).catch((err) => {
      if (deliveryOptionsRequestRef.current !== requestId) return;
      setDeliveryOptionsStatus("error");
      console.warn("[RequestChangeModal] booked car delivery options fetch failed:", err);
    });
  };

  useEffect(() => {
    if (!open || !trip) {
      setDeliveryOptionsStatus("idle");
      return;
    }
    fetchBookedCarDeliveryOptions();
    return () => {
      deliveryOptionsRequestRef.current++;
    };
    // `fetchBookedCarDeliveryOptions` is intentionally invoked only when the
    // booking context changes; its state setters and request ref are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, trip?.id, token, trip?.is_delivery, trip?.airport_code, trip?.delivery_distance, tripRentalType]);

  // Availability fetch (mirrors ConversationPage task #1646).
  useEffect(() => {
    if (!open) return;
    const carId = car?.id ?? null;
    const carSlug = car?.slug ?? null;
    if (!carId || !carSlug) return;
    if (availabilityCarIdRef.current !== carId) {
      setAvailability(null);
      availabilityAttemptedRef.current = null;
    }
    if (availabilityAttemptedRef.current === carId) return;
    availabilityCarIdRef.current = carId;
    availabilityAttemptedRef.current = carId;
    const requestedCarId = carId;
    setLoadingAvailability(true);
    // Task #2880 — pass the current trip id so the server omits this
    // booking from `blockedDates`, letting the renter/host keep or
    // adjust around the original dates in the change-request calendar.
    api.getCarAvailability(carId, carSlug, { excludeTripId: trip?.id ?? null, token })
      .then((data) => {
        if (availabilityCarIdRef.current !== requestedCarId) return;
        setAvailability(data);
      })
      .catch(() => {
        if (availabilityCarIdRef.current !== requestedCarId) return;
        setAvailability(null);
      })
      .finally(() => {
        if (availabilityCarIdRef.current !== requestedCarId) return;
        setLoadingAvailability(false);
      });
  }, [open, car?.id, car?.slug]);

  // Task #1676: one-shot reverse geocode for delivery trips. Updates BOTH
  // the editable proposed address (so the location editor pre-fills with
  // the resolved street) AND a separate `resolvedOriginalAddress` cache
  // used by the Original column — without the latter, a coords-only trip
  // would render "Delivery to your address" on the left and the resolved
  // street on the right, which read like an unintended change.
  const [resolvedOriginalAddress, setResolvedOriginalAddress] = useState<string | null>(null);
  useReverseGeocodePrefill({
    enabled: open,
    isDelivery,
    airportCode,
    address: deliveryAddress,
    lat: pickupLat,
    lng: pickupLng,
    onResolved: (displayName) => {
      setDeliveryAddress((prev) => prev || displayName);
      setResolvedOriginalAddress((prev) => prev || displayName);
    },
    onReset: () => setResolvedOriginalAddress(null),
  });

  // Live recalculated proposed quote with debounced refetch +
  // race-protection. Errors surface inline (`quoteError`) and disable
  // Send Proposal so we never let the user post a proposal we couldn't
  // price.
  //
  // Task #2959 — host-typed rental / delivery fee overrides are now
  // forwarded to the server quote endpoint as `rental_fee_override`
  // / `delivery_fee_override`. That runs the same canonical
  // `calculateQuote` path the accept handler uses, so the modal's
  // Proposed column shows the exact serviceFee / ownerEarn /
  // paymentDue numbers the server will persist on accept — instead
  // of the previous client-side rate back-derivation (which drifted
  // by 1p on clean 15% rates and worse on any quote carrying a
  // refundable surcharge, coupon, or pro-owner share). While a
  // re-quote is in flight the previous server quote stays mounted
  // (no flash of half-computed numbers) and Send is disabled via
  // `sendDisabled` below.
  useEffect(() => {
    if (!open || !trip?.id || !dateFrom || !dateTo) {
      setQuote(null);
      setQuoteError(null);
      return;
    }
    if (!token) {
      setQuoteError({ message: t("conversation.quote_auth_required", "Sign in again to price this change request.") });
      setQuoteLoading(false);
      return;
    }
    const version = ++quoteVersionRef.current;
    setQuoteLoading(true);
    setQuoteError(null);
    const handle = setTimeout(() => {
      // Owner-only fee overrides. Empty strings / non-finite values
      // mean "no override" — fall through to the calculator's
      // car-priced numbers exactly as before.
      const rentalOverrideNum = Number(rentalFee);
      const deliveryOverrideNum = Number(deliveryFee);
      const rentalOverrideParam = isOwner && rentalFeeTouched && rentalFee !== "" && Number.isFinite(rentalOverrideNum)
        ? rentalOverrideNum
        : undefined;
      const deliveryOverrideParam = isOwner && isDelivery && deliveryFeeTouched && deliveryFee !== "" && Number.isFinite(deliveryOverrideNum)
        ? deliveryOverrideNum
        : undefined;
      // Task #4599 — chauffeured pricing reads only the explicit `hours`
      // param server-side (it does NOT derive hours from pickup/dropoff
      // times) and falls back to a hard-coded 8 when absent. Forward the
      // trip's real booked hours, snapped exactly like the BookingWidget,
      // so the chauffeured rental fee, the minimum-fee floor shown to the
      // host, and the host-override block all reflect the actual duration
      // (e.g. 2h for a 10am–12pm trip) instead of an inflated 8h. Scoped
      // to chauffeured: self-drive / event stay day-based with no hours.
      const hoursParam = tripRentalType === "chauffeured"
        ? deriveChauffeuredHours(pickupTime, dropoffTime)
        : undefined;
      bookingsApi
        .getTripAdjustmentQuote(token, trip.id, {
          dateFrom,
          dateTo,
          rentalType: tripRentalType,
          hours: hoursParam,
          pickupAt: composeAdjIso(dateFrom, pickupTime),
          dropoffAt: composeAdjIso(dateTo, dropoffTime),
          isDelivery: hasStarted ? origIsDelivery : isDelivery,
          deliveryDistance: hasStarted
            ? (trip.delivery_distance ?? null)
            : (isDelivery && !airportCode ? deliveryDistance : null),
          airportCode: hasStarted ? (trip.airport_code ?? null) : (isDelivery ? airportCode : null),
          includeFloors: isOwner,
          rentalFeeOverride: rentalOverrideParam,
          deliveryFeeOverride: deliveryOverrideParam,
          pickupAddress: hasStarted
            ? (trip.delivery_address ?? null)
            : (isDelivery && !airportCode ? deliveryAddress : null),
          pickupLatitude: hasStarted
            ? (trip.pickup_latitude ?? null)
            : (isDelivery && !airportCode ? pickupLat : null),
          pickupLongitude: hasStarted
            ? (trip.pickup_longitude ?? null)
            : (isDelivery && !airportCode ? pickupLng : null),
        })
        .then((q) => {
          if (quoteVersionRef.current !== version) return;
          setQuote(q);
          setQuotedTermsKey(quoteTermsKey);
          setQuoteError(null);
        })
        .catch((err) => {
          if (quoteVersionRef.current !== version) return;
          // Task #2910 — preserve the typed shape so the Price section
          // can render code-aware copy (delivery-radius, airport, past
          // dates, missing pricing, …) instead of the old hardcoded
          // "adjust the dates" string. For unknown errors we still
          // surface the server's human-readable message verbatim.
          if (err instanceof QuoteApiError) {
            setQuoteError({ message: err.message, code: err.code, maxRadius: err.maxRadius });
          } else {
            const msg = err instanceof Error ? err.message : String(err);
            setQuoteError({ message: msg });
          }
          // Task #2959 — keep the previous server quote mounted on
          // error so the Proposed column shows the last-good numbers
          // until the host fixes the offending input. Send is gated
          // by `quoteError` (see `sendDisabled`) so they can't post a
          // stale preview either.
        })
        .finally(() => {
          if (quoteVersionRef.current !== version) return;
          setQuoteLoading(false);
        });
    }, 350);
    return () => {
      clearTimeout(handle);
      quoteVersionRef.current++;
    };
    // `isInProgress` / `trip?.id` are intentionally omitted — they're
    // stable across the modal's lifetime (the trip identity / status
    // doesn't flip while the modal is mounted) and listing them here
    // would also create a TDZ since `isInProgress` is declared below.
  }, [open, trip?.id, trip?.status, trip?.started_at, trip?.pick_up, dateFrom, dateTo, pickupTime, dropoffTime, tripRentalType, isDelivery, deliveryDistance, airportCode, deliveryAddress, pickupLat, pickupLng, token, isOwner, quoteTermsKey]);

  // Owner-only: auto-fill fee inputs from the live quote when the
  // *quote itself* produces a new number, scoped per-input.
  //
  // Task #2655 — previous shape gated on `proposalEdited` only, which
  // meant the very first edit to either fee input (which flips
  // `proposalEdited` true without changing the quote) re-ran this
  // effect and blasted the *other* fee with `quote.rentalFee` /
  // `quote.deliveryFee`. Concretely: opening the modal with rental
  // £110 and then typing into Delivery rewrote Rental to the car's
  // current day-rate (£200). The fix is to only auto-fill when the
  // underlying quote number genuinely changed (a re-quote triggered
  // by dates / times / location), and to scope each input to its own
  // field — editing one fee never touches the other.
  const prevQuoteSnapshotRef = useRef<{ rentalFee: number; deliveryFee: number } | null>(null);
  useEffect(() => {
    if (!open) {
      prevQuoteSnapshotRef.current = null;
      return;
    }
    if (!quote || quoteLoading || quoteError || quotedTermsKey !== quoteTermsKey) return;
    const prev = prevQuoteSnapshotRef.current;
    const nextRental = Number(quote.rentalFee) || 0;
    const nextDelivery = Number(quote.deliveryFee || 0);
    const rentalChanged = prev != null && prev.rentalFee !== nextRental;
    const deliveryChanged = prev != null && prev.deliveryFee !== nextDelivery;
    prevQuoteSnapshotRef.current = { rentalFee: nextRental, deliveryFee: nextDelivery };
    // Mirror-mode guard: until the host has actively edited something
    // (or a counter-proposal seed pre-populates the inputs) we leave
    // the seeded original values alone. Same intent as before, just
    // expressed alongside the per-input change detection.
    if (!proposalEdited) return;
    if (rentalChanged && !rentalFeeTouched) {
      setRentalFee(quote.rentalFee != null ? Number(quote.rentalFee).toFixed(2) : "");
    }
    if (deliveryChanged && isDelivery && !deliveryFeeTouched) {
      setDeliveryFee(quote.deliveryFee && quote.deliveryFee > 0 ? Number(quote.deliveryFee).toFixed(2) : "");
    }
  }, [open, quote, quoteLoading, quoteError, quotedTermsKey, quoteTermsKey, proposalEdited, rentalFeeTouched, deliveryFeeTouched, isDelivery]);

  // Task #4738 — pending request-to-book trips (and host-sent build-a-trip
  // offers) never persist `trips.rental_fee` / `trips.delivery_fee`, so the
  // fee inputs open EMPTY (placeholder only) — the host couldn't see or edit
  // the amount they were about to propose. Committed trips prefill from the
  // stored fee at open; pending requests instead seed from their immutable
  // original financial snapshot, in mirror mode. It only fills an untouched input
  // — once the host types, `*Touched` freezes it; once any quote-affecting
  // input changes (`proposalEdited`), the auto-fill effect above takes over.
  // The seeded value flows through the submit payload (line ~1336) exactly
  // like a committed trip's prefilled fee, so the override persists.
  useEffect(() => {
    if (!open || !isOwner) return;
    if (trip?.status !== "pending") return; // committed trips prefill from stored fee
    if (proposalEdited) return; // host started editing — auto-fill effect drives
    if (!baselineSnapshot) return;
    if (!rentalFeeTouched && seed?.rentalFee == null && baselineSnapshot.rentalFee != null
      && rentalFee !== Number(baselineSnapshot.rentalFee).toFixed(2)) {
      setRentalFee(Number(baselineSnapshot.rentalFee).toFixed(2));
    }
    if (!deliveryFeeTouched && seed?.deliveryFee == null && isDelivery && baselineSnapshot.deliveryFee != null
      && deliveryFee !== Number(baselineSnapshot.deliveryFee).toFixed(2)) {
      setDeliveryFee(Number(baselineSnapshot.deliveryFee).toFixed(2));
    }
  }, [open, isOwner, trip?.status, proposalEdited, baselineSnapshot, rentalFeeTouched, deliveryFeeTouched, rentalFee, deliveryFee, isDelivery, seed?.rentalFee, seed?.deliveryFee]);

  const rentalFloor = useMemo(
    () => computeMinRentalFee(quote, trip?.rental_fee),
    [quote, trip?.rental_fee],
  );
  const minRentalFee = rentalFloor.amount;
  // Owner can't bypass the floor: when the override is below the
  // computed minimum we block. We also block when the owner has
  // touched the override but the floors snapshot wasn't returned by
  // the server — degrading to "no floor" would let them undercut the
  // per-car minimum, so we hard-fail until the floors arrive.
  const rentalFeeBelowFloor = useMemo(() => {
    if (!isOwner) return false;
    if (!rentalFeeTouched) return false;
    if (!rentalFee) return false;
    const n = Number(rentalFee);
    if (!Number.isFinite(n)) return false;
    if (!rentalFloor.available) return true;
    return n < rentalFloor.amount;
  }, [isOwner, rentalFeeTouched, rentalFee, rentalFloor]);
  // Task #2692 — per-row source used by the Proposed column's fee
  // badges. Resolves to:
  //   • "owner"  when the viewer (host) has edited this row in the
  //     current counter; also when the seeded value came from the host.
  //   • "guest"  when the row was seeded from the other party's
  //     proposal and the viewer hasn't edited it yet (host counters
  //     a guest's proposal: show the guest's number with a
  //     "GUEST-PROPOSED" tag until the host changes it).
  //   • null     when there's nothing meaningful to label — neither
  //     party explicitly proposed an override on this row.
  //
  // Task #2959 — dropped the "rentalFeeIsOverride" heuristic that
  // compared the typed input to `quote.rentalFee`. Now that the
  // Proposed column reads from a server quote that already reflects
  // the typed overrides, `quote.rentalFee` always equals the input —
  // the comparison would never fire. Direct-edit and seed-source
  // signals fully cover the badge logic.
  const proposedRentalFeeSource = useMemo<"guest" | "owner" | null>(() => {
    if (rentalFeeOwnerEdited) return isOwner ? "owner" : "guest";
    if (rentalFeeSeedSource) return rentalFeeSeedSource;
    return null;
  }, [rentalFeeOwnerEdited, rentalFeeSeedSource, isOwner]);
  const proposedDeliveryFeeSource = useMemo<"guest" | "owner" | null>(() => {
    if (deliveryFeeOwnerEdited) return isOwner ? "owner" : "guest";
    if (deliveryFeeSeedSource) return deliveryFeeSeedSource;
    return null;
  }, [deliveryFeeOwnerEdited, deliveryFeeSeedSource, isOwner]);

  // Build the proposed quote actually rendered to the user — when an
  // owner overrides rental_fee or delivery_fee, we splice the override
  // into the live quote and re-derive `totalIncludingTax` / `paymentDue`
  // by adjusting for the delta. This keeps the rolled-up summary in
  // sync with what the proposal payload will actually send.
  // Task #2583 — once the trip is underway only the end date / dropoff
  // time can move. The location card, the start half of the dates
  // editor, and the owner fee inputs all freeze, and the submit handler
  // re-asserts the locked values before posting in case anything slipped
  // through. The server enforces the same rules.
  // Task #2940 follow-up — `status` is not always flipped the moment
  // pickup time passes (a cron may run minutes/hours later, or the
  // status may remain "confirmed" until the host confirms pickup).
  // For change-request purposes, treat any trip whose pickup time has
  // passed and whose drop-off is still in the future as in-progress.
  //
  // Task #2974 — split the single "in-progress" flag into two:
  //   * `hasStarted` — pickup instant has passed, regardless of whether
  //     the drop-off is still in the future. This is the gate that
  //     freezes the start date/time, pickup location and fee inputs.
  //     Once the original pickup is in the past those values can't be
  //     retroactively moved, even on a same-day booking whose drop-off
  //     also already passed (the renter is then proposing an extension,
  //     not a do-over).
  //   * `isInProgress` — pickup has passed AND drop-off is still in
  //     the future. Reserved for live-trip-only behavior such as the
  //     30-minute end-soon buffer and the "trip is in progress" copy.
  const isAdjustableTripStatus = ["confirmed", "approved", "accepted", "trip_confirmed"].includes(String(trip?.status || ""));
  const hasStarted = useMemo(() => {
    if (trip?.status === "in_progress" || trip?.status === "active") return true;
    if (!isAdjustableTripStatus) return false;
    // Eligibility is server-projected; display stamps are not physical instants.
    return trip?.bookingClock?.started ?? true;
  }, [trip?.status, trip?.bookingClock?.started, isAdjustableTripStatus]);
  const isInProgress = useMemo(() => {
    if (!hasStarted) return false;
    return trip?.bookingClock?.inProgress ?? false;
  }, [hasStarted, trip?.bookingClock?.inProgress]);
  const originalStartDay = useMemo(() => {
    // Task #3017: started_at is the source of truth; pick_up is a transitional fallback only.
    const p = getTripPickupIso(trip);
    return p ? String(p).split("T")[0] : "";
  }, [trip?.pick_up, trip?.started_at]);
  // Task #3017: started_at is the source of truth; pick_up is a transitional fallback only.
  const originalPickupHHMM = useMemo(() => prefillAdjTime(getTripPickupIso(trip), trip?.times_captured ?? null), [trip?.pick_up, trip?.started_at, trip?.times_captured]);
  const inProgressEndsSoon = useMemo(() => {
    if (!isInProgress || !trip?.drop_off) return false;
    const msToEnd = Date.parse(trip.bookingClock?.return ?? "") - Date.now();
    return msToEnd <= 30 * 60 * 1000;
  }, [isInProgress, trip?.drop_off]);
  // Task #2583 — for in-progress trips, the new drop-off must be in the
  // future. The trip started in the past so `dateFrom` (= originalStartDay)
  // would let the user pick a day that's already gone. Floor at today.
  const todayLocalDay = useMemo(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }, []);
  const nowHHMM = useMemo(() => {
    const d = new Date();
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  }, []);

  // Task #2959 — the Proposed column now reads directly from the
  // server quote. Host rental / delivery fee overrides are forwarded
  // to `/bookings/quote` as `rental_fee_override` /
  // `delivery_fee_override`, and the same canonical `calculateQuote`
  // the accept handler runs returns the authoritative serviceFee /
  // baseRentalFee / paymentDue / ownerEarn — so the preview matches
  // the persisted numbers to the penny. The old `overriddenQuote`
  // memo back-derived the service-fee rate from the original quote
  // and the owner share from `ownerEarn / (baseRental + delivery)`,
  // both of which drifted by 1p on clean 15% rates and worse on any
  // quote carrying a refundable surcharge / coupon / pro-owner share.

  // Task #2610 — synthesize the Original column summary directly from
  // the booking record (last-agreed) or, when a proposal is pending,
  // from that proposal (last-suggested). No re-quoting against the
  // car's current rates: those numbers could drift from what the host
  // and guest actually committed to, and during a negotiation they'd
  // also hide whatever the most recent counter just offered.
  //
  // "Last agreed" = trip's stored `rental_fee` / `service_fee` /
  //   `insurance_fee` / `delivery_fee` (and `payment_due` for the
  //   anchored total). Filled at booking creation and updated by the
  //   server only when an accepted adjustment lands.
  // "Last suggested" = the fee fields on the most-recent pending
  //   `trip_adjustments` row, passed in via `pendingProposal`.
  //
  // Climate-offset and refundable-cancellation surcharges are
  // intentionally absent — they're renter-side checkout add-ons,
  // never part of a host/guest fee negotiation.
  // Pending requests may lack root money fields. Read originating persisted
  // evidence, or independently quote the ORIGINAL terms; never freeze a
  // proposal response. Hold the snapshot for this open/trip even if the
  // parent refreshes its object or the user edits before the read completes.
  useEffect(() => {
    setBaselineSnapshot(null);
    setBaselineLoading(false);
    if (!open || !trip || trip.status !== "pending" || !token || !isOwner) return;
    if ([trip.rental_fee, trip.delivery_fee, trip.service_fee, trip.insurance_fee, trip.payment_due, trip.owner_earn]
      .every(value => knownMoney(value) != null)) return;
    // Capture original terms, never mutable form state or a counter seed.
    const pickup = getTripPickupIso(trip);
    const dropoff = trip.drop_off;
    let cancelled = false;
    setBaselineLoading(true);
    void (async () => {
      const read = bookingsApi.getInitialRequestFinancialSnapshot;
      const persisted = typeof read === "function"
        ? (await read.call(bookingsApi, token, trip.id)).snapshot
        : null;
      let snapshot = persisted;
      if (!hasRequestFinancialSnapshot(snapshot) && pickup && dropoff) {
        snapshot = await bookingsApi.getTripAdjustmentQuote(token, trip.id, {
          dateFrom: pickup.slice(0, 10), dateTo: dropoff.slice(0, 10),
          pickupAt: pickup, dropoffAt: dropoff, rentalType: tripRentalType,
          hours: tripRentalType === "chauffeured"
            ? deriveChauffeuredHours(prefillAdjTime(pickup, trip.times_captured), prefillAdjTime(dropoff, trip.times_captured))
            : undefined,
          isDelivery: !!trip.is_delivery || !!trip.airport_code || Number(trip.delivery_distance) > 0,
          deliveryDistance: trip.delivery_distance, airportCode: trip.airport_code,
          pickupAddress: trip.delivery_address, pickupLatitude: trip.pickup_latitude,
          pickupLongitude: trip.pickup_longitude,
          rentalFeeOverride: knownMoney(trip.rental_fee) ?? undefined,
          deliveryFeeOverride: knownMoney(trip.delivery_fee) ?? undefined,
        });
      }
      if (!cancelled && hasRequestFinancialSnapshot(snapshot)) setBaselineSnapshot(snapshot);
    })().catch(() => {
      // Retain root evidence; an unavailable original must not be replaced
      // by a later proposal quote or treated as a zero-payout request.
    }).finally(() => { if (!cancelled) setBaselineLoading(false); });
    return () => { cancelled = true; };
  }, [open, trip?.id, trip?.status, token, tripRentalType, isOwner]);
  const baselineQuoteSummary = useMemo(() => summariseQuote(baselineSnapshot, isOwner), [baselineSnapshot, isOwner]);

  const originalSummary = useMemo<{ total: number; breakdown: PriceLine[] } | null>(() => {
    if (!trip) return null;
    // Only pending requests may use the independent original snapshot.
    // An unavailable baseline stays unknown, never a fabricated zero.
    if (trip.status === "pending" && baselineQuoteSummary) return baselineQuoteSummary;
    if (trip.rental_fee == null) {
      return trip.status === "pending" ? baselineQuoteSummary : null;
    }
    // Task #2903 — the Original column is the canonical "committed
    // trip" snapshot: rental / service / insurance / delivery /
    // total all come from the trip row (the same fields the
    // Reservation panel renders), never from a pending /
    // withdrawn / stale `trip_suggestions` row. This is the
    // single-baseline contract that pins the modal Original, the
    // unedited Proposed delta, and the chat-card "was £X" to the
    // same number. Anything a host wants to *counter against* is
    // surfaced via the Proposed column's seed (and the OWNER /
    // GUEST-PROPOSED badges), not by overwriting Original.
    const rental = Number(trip.rental_fee ?? 0) || 0;
    const service = Number(trip.service_fee ?? 0) || 0;
    const insurance = Number(trip.insurance_fee ?? 0) || 0;
    const delivery = Number(trip.delivery_fee ?? 0) || 0;
    const componentSum = Math.round((rental + service + insurance + delivery) * 100) / 100;
    const committedTotal = getCommittedTripTotal(trip);
    const total = !isOwner && committedTotal > 0
      ? Math.round(committedTotal * 100) / 100
      : componentSum;
    // Reconcile any pennies of drift between the stored
    // `payment_due` anchor and the component sum into the rental
    // line, so the rendered breakdown always sums to the
    // displayed total (legacy rows that pre-date `payment_due`
    // have committedTotal === 0 and skip this).
    if (!isOwner) return {
      total,
      breakdown: buildPriceBreakdownLines({ rentalFee: rental, serviceFee: service, insuranceFee: insurance,
        deliveryFee: delivery, numDays: 1, insurancePackageName: trip.insurance_package_name }, {
        canonicalTotal: total, proOwnerDiscount: trip.appliedDiscount?.pro_owner_discount ?? 0,
        proOwnerTier: trip.appliedDiscount?.pro_owner_tier,
        couponDiscount: trip.appliedDiscount?.coupon_amount ?? 0,
        couponCode: trip.appliedDiscount?.coupon_code,
      }),
    };
    const residue = Math.round((total - componentSum) * 100) / 100;
    const reconciledRental = Math.round((rental + residue) * 100) / 100;
    // Line order: rental → insurance → delivery → service_fee.
    const breakdown: PriceLine[] = [
      { key: "rental_fee", amount: reconciledRental },
    ];
    if (insurance > 0) breakdown.push({ key: "insurance", amount: insurance, detail: trip.insurance_package_name ?? null });
    if (delivery > 0) breakdown.push({ key: "delivery", amount: delivery });
    if (service > 0) breakdown.push({ key: "service_fee", amount: service });
    return { total, breakdown };
  }, [trip, currency, baselineQuoteSummary, isOwner]);
  // Task #2959 — derive directly from the server quote (which now
  // already reflects any host rental / delivery overrides via the
  // `rental_fee_override` / `delivery_fee_override` query params).
  const currentQuote = quotedTermsKey === quoteTermsKey && !quoteLoading && !quoteError ? quote : null;
  const proposedSummary = useMemo(() => summariseQuote(currentQuote, isOwner), [currentQuote, isOwner]);

  // Task #2655 — baseline number the rental-fee hint reads from before
  // the host has touched a quote-affecting input. Pulled from the
  // Original column's reconciled `rental_fee` breakdown row so the two
  // surfaces always agree on "what this trip is currently priced at".
  const rentalFeeHintOriginal = useMemo<number | null>(() => {
    if (!originalSummary) return null;
    const row = originalSummary.breakdown.find((b) => b.key === "rental_fee");
    return row ? row.amount : null;
  }, [originalSummary]);

  // Task #2641 / #2737 — until the viewer has actively edited a
  // quote-affecting input the Proposed column mirrors the Original
  // (i.e. the persisted proposal row or the trip's last-agreed
  // fees), so the modal matches the inline proposal card to the
  // penny. This prevents the initial flicker where the
  // live-recalculated quote would briefly replace the persisted
  // numbers, AND avoids the host/renter share mismatch that fell out
  // of `overriddenQuote` re-deriving ownerEarn with a 0.80 default.
  // Once `proposalEdited` flips true (any date / time / location /
  // fee edit) the live quote takes over as before.
  //
  // Originally host-only (#2641), the mirror now applies to renters
  // too so a guest countering a host's proposal sees the same total
  // on the card and in the Proposed column. Renters still have no
  // fee inputs to override, so the live re-quote only ever surfaces
  // for them after a date / time / location edit.
  const effectiveProposedSummary = useMemo<{ total: number; breakdown: PriceLine[] } | null>(() => {
    if (proposalEdited) return proposedSummary;
    // A failed original read does not make a valid proposal quote disappear.
    // This is display-only: never promote that quote into Original.
    return originalSummary ?? (baselineLoading ? null : proposedSummary);
  }, [proposalEdited, proposedSummary, originalSummary, baselineLoading]);

  // Task #2641 / #2903 — host payout values. For hosts only:
  //   • Original  = the trip's stored `owner_earn` (the committed
  //     payout the host has been quoted to take home), routed
  //     through `getCommittedOwnerEarn` for symmetry with the
  //     renter-facing `getCommittedTripTotal`. Task #2903 explicitly
  //     drops the pending-proposal fallback that previously fed
  //     this value — a stale / withdrawn / inflated pending row
  //     can no longer inflate the host's Original earnings line
  //     above what the OwnerBookingDetailPage renders.
  //   • Proposed  = the live server quote's payout. Task #2959 —
  //     this is now the server-canonical `quote.ownerEarn`, computed
  //     by the same `calculateQuote` path the accept handler runs
  //     (so override rentals / deliveries flow through with the
  //     booking's real ownerShare, not a re-derived 0.80 default).
  //     While the proposal hasn't been edited yet we mirror the
  //     Original value so the row reconciles with the mirrored
  //     Proposed total.
  const originalOwnerEarn = useMemo(() => {
    if (!isOwner) return null;
    if (trip?.status === "pending" && baselineSnapshot) return knownMoney(baselineSnapshot.ownerEarn);
    return knownMoney(trip?.owner_earn);
  }, [isOwner, trip, baselineSnapshot]);
  const proposedOwnerEarn = useMemo(() => {
    if (!isOwner) return null;
    if (!proposalEdited && originalSummary) return originalOwnerEarn;
    if (!proposalEdited && baselineLoading) return null;
    return knownMoney(currentQuote?.ownerEarn);
  }, [isOwner, proposalEdited, currentQuote, originalOwnerEarn, originalSummary, baselineLoading]);

  const locationPolicy = getRequestChangeLocationPolicy({
    rentalType: tripRentalType,
    isOwner,
    isDelivery,
    carDeliveryEnabled,
    selfDriveDeliveryEnabled,
  });

  if (!open || !trip) return null;

  const pickupDefault = "10:00 am";
  const dropoffDefault = tripRentalType === "driving" ? "10:00 pm" : "12:00 pm";
  const pickup12 = hhmmTo12h(pickupTime, pickupDefault);
  const dropoff12 = hhmmTo12h(dropoffTime, dropoffDefault);

  // Task #2692 — when a pending proposal is in play, the Original
  // column must show what's being countered (the proposal's own
  // dates / times / location), not the last-agreed trip values. Each
  // field falls back to the trip when the proposal didn't change it,
  // so legacy rows that only carry fee fields still render correctly.
  // Task #2903 — Original dates / times come from the committed
  // trip row, never from a pending / withdrawn `trip_suggestions`
  // row. What's being countered is surfaced via the Proposed
  // column's seed, not by overwriting Original.
  const origPickUpRaw = getTripPickupIso(trip);
  const origDropOffRaw = trip.drop_off ?? null;
  const origTimesCaptured = trip.times_captured;
  const origDateFrom = origPickUpRaw ? origPickUpRaw.split("T")[0] : "";
  const origDateTo = origDropOffRaw ? origDropOffRaw.split("T")[0] : "";
  const origPickup12 = hhmmTo12h(prefillAdjTime(origPickUpRaw, origTimesCaptured), pickupDefault);
  const origDropoff12 = hhmmTo12h(prefillAdjTime(origDropOffRaw, origTimesCaptured), dropoffDefault);
  const origDatesSummary = buildDateTimeSummary(origDateFrom, origDateTo, origPickup12, origDropoff12, t as (k: string, fb?: string) => string);
  const proposedDatesSummary = buildDateTimeSummary(dateFrom, dateTo, pickup12, dropoff12, t as (k: string, fb?: string) => string);

  const country = car?.country_code ?? undefined;
  const distanceUnit = getDistanceUnitPlural(country);

  const renderLocationSummary = (
    isDel: boolean,
    addr: string | null,
    apCode: string | null,
    distance: number | null,
  ) => {
    const airport = apCode ? carDeliveryAirports.find((a) => a.airport_code === apCode) : null;
    const summary = getLocationSummary({
      isDelivery: isDel,
      address: addr,
      airportCode: apCode,
      airportName: airport?.airport_name ?? null,
      distance,
      distanceUnit,
      carCity: car?.city,
      carState: car?.state,
      selfPickupLabel: t("checkout.pickup_at_car_location", "Free pickup at the car's location"),
      t: (key, fallback, opts) => String(t(key, fallback, opts)),
    });
    if (!summary) {
      return (
        <p className="text-sm text-brand">
          {isDel
            ? t("checkout.delivery_to_address", "Delivery to your address")
            : t("checkout.pickup_at_car_location", "Free pickup at the car's location")}
        </p>
      );
    }
    return (
      <>
        <p className="text-sm text-brand">{summary.primary}</p>
        {summary.secondary && (
          <p className="mt-0.5 text-[11px] text-[#8a9a8a]">{summary.secondary}</p>
        )}
      </>
    );
  };

  // Task #2903 — Original location comes from the committed trip
  // row, never from a pending / withdrawn `trip_suggestions` row.
  // What's being countered is surfaced via the Proposed column's
  // seed (and the OWNER / GUEST-PROPOSED badges), not by
  // overwriting Original.
  const origIsDelivery = hasPersistedTripDelivery(trip);
  const origAddress = trip.delivery_address ?? resolvedOriginalAddress;
  const origAirportCode = trip.airport_code ?? null;
  const origDeliveryDistance = trip.delivery_distance ?? null;

  // Task #2610 — originalSummary is always derived synchronously from
  // the trip / pending proposal, so the previous bare-fallback path is
  // no longer reachable. Total comes straight from the synthesized
  // summary; the proposed-vs-original delta still uses the live quote.
  const origTotal = originalSummary?.total ?? 0;
  // Task #2641 — the displayed Proposed total + the +£X vs. original
  // delta both read from `effectiveProposedSummary`, which mirrors the
  // Original until the host edits something. So before any edits the
  // delta is naturally £0 (Proposed === Original), and after the first
  // edit it falls back to the live-quote-derived totals as before.
  const proposedTotal = effectiveProposedSummary?.total ?? null;
  // Task #2654 — the "vs. original" delta under the Proposed column must
  // reflect the number the *viewer* cares about, not always the renter's
  // grand total. For hosts that's their earnings (proposed − original
  // `Your earnings`); for renters it stays the trip total. Sign
  // convention is unchanged: positive = number went up. Below, hosts
  // flip the colour mapping so a *drop* in earnings reads as the "bad
  // for me" amber colour and a *rise* reads as the emerald "good for
  // me" colour, mirroring how renters see an *increase* in total as
  // amber (bad) and a *decrease* as emerald (good).
  const totalDelta = proposedTotal != null ? proposedTotal - origTotal : null;
  const ownerEarnDelta = isOwner && originalSummary && effectiveProposedSummary
    && proposedOwnerEarn != null && originalOwnerEarn != null
    ? proposedOwnerEarn - originalOwnerEarn : null;
  const viewerDelta = isOwner ? ownerEarnDelta : totalDelta;
  const viewerDeltaIsGood = isOwner
    ? (viewerDelta != null && viewerDelta > 0)
    : (viewerDelta != null && viewerDelta < 0);

  // Task #3531 — for a renter on a partial-pay booking the "+X vs
  // original" preview must split into what moves on the card now (the
  // platform portion) and what changes in the owner-direct amount paid
  // at pickup (`owner_earn`). The committed owner-direct portion comes
  // from the trip; the proposed one from the live quote once edited.
  // Full-pay renters and hosts skip this entirely (handled above).
  const isPartialPay = (trip?.payment_type ?? "full") === "partial";
  const renterOriginalOwnerEarn = getCommittedOwnerEarn(trip);
  const renterProposedOwnerEarn = proposalEdited && quote?.ownerEarn != null
    ? (Number(quote.ownerEarn) || 0)
    : renterOriginalOwnerEarn;
  const renterPartialDelta = (!isOwner && isPartialPay && proposedTotal != null)
    ? computeRenterAdjustmentDelta({
        currency,
        isPartial: true,
        currentTotal: origTotal,
        proposedTotal,
        currentOwnerEarn: renterOriginalOwnerEarn,
        proposedOwnerEarn: renterProposedOwnerEarn,
      })
    : null;

  /**
   * Render itemised breakdown rows from a summary. Inlined here so
   * renter and owner can compare lines side by side.
   */
  const renderBreakdownRows = (
    summary: { breakdown: PriceLine[] } | null,
    opts?: {
      fallback?: { rental: number; delivery: number };
      // Task #2692 — fee rows now carry a per-row badge describing who
      // proposed the current value: `"owner"` shows "OWNER-PROPOSED",
      // `"guest"` shows "GUEST-PROPOSED", and `null` shows no badge.
      // The Proposed column passes a seed-derived value on first open
      // (e.g. `"guest"` when a host is countering a guest's request),
      // then flips to the viewer's own role once they edit the input.
      feeSources?: { rental: "guest" | "owner" | null; delivery: "guest" | "owner" | null };
    },
  ) => {
    const feeSources = opts?.feeSources;
    const fallback = opts?.fallback;
    const renderBadge = (source: "guest" | "owner") => (
      source === "owner" ? (
        <span
          className="ml-2 rounded-full bg-brand/10 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-brand"
          data-testid="rcm-owner-proposed-badge"
        >
          {t("conversation.owner_proposed", "Owner-proposed")}
        </span>
      ) : (
        <span
          className="ml-2 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-amber-800"
          data-testid="rcm-guest-proposed-badge"
        >
          {t("conversation.guest_proposed_fee", "Guest-proposed")}
        </span>
      )
    );
    if (!summary && fallback) {
      return (
        <div className="space-y-1.5 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-[#5a6b5a]">{t("conversation.fee_rental")}</span>
            <span className="tabular-nums text-brand">{formatMoneyWithCurrency(fallback.rental, currency)}</span>
          </div>
          {fallback.delivery > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-[#5a6b5a]">{t("conversation.fee_delivery")}</span>
              <span className="tabular-nums text-brand">{formatMoneyWithCurrency(fallback.delivery, currency)}</span>
            </div>
          )}
        </div>
      );
    }
    if (!summary) return null;
    return (
      <div className="space-y-1.5 text-sm">
        {summary.breakdown.map((line) => {
          const source: "guest" | "owner" | null = line.key === "rental_fee"
            ? (feeSources?.rental ?? null)
            : line.key === "delivery"
              ? (feeSources?.delivery ?? null)
              : null;
          return (
            <div key={line.key} className="flex items-center justify-between">
              <span className="text-[#5a6b5a]">
                {t(`pricing.line.${line.key}`)}
                {line.detail ? <span className="text-[#8a9a8a]"> · {line.detail}</span> : null}
                {source && renderBadge(source)}
              </span>
              <span className="tabular-nums text-brand">{formatMoneyWithCurrency(line.amount, currency)}</span>
            </div>
          );
        })}
      </div>
    );
  };

  async function handleSubmit() {
    if (!trip?.id || !token || !dateFrom || !dateTo) return;
    if (!currentQuote) return;
    const fromDate = new Date(dateFrom);
    const toDate = new Date(dateTo);
    if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
      setError(t("conversation.valid_dates_required"));
      return;
    }
    if (toDate.getTime() < fromDate.getTime()) {
      setError(t("conversation.dropoff_after_pickup"));
      return;
    }
    const fees = { rental_fee: rentalFee, delivery_fee: deliveryFee };
    for (const [k, v] of Object.entries(fees)) {
      if (v && (!isFinite(Number(v)) || Number(v) < 0)) {
        const fieldLabel = k === "rental_fee" ? t("conversation.field_rental_fee") : t("conversation.field_delivery_fee");
        setError(t("conversation.invalid_amount", { field: fieldLabel }));
        return;
      }
    }
    if (isOwner && rentalFeeBelowFloor) {
      if (!rentalFloor.available) {
        setError(t(
          "conversation.rental_fee_floor_unavailable",
          "We can't confirm the minimum rental fee for this trip yet. Please try again in a moment.",
        ));
      } else {
        setError(t(
          "conversation.rental_fee_below_min",
          "Rental fee must be at least {{min}} for this trip.",
          { min: formatMoneyWithCurrency(minRentalFee, currency) },
        ));
      }
      return;
    }
    if (isInProgress && inProgressEndsSoon) {
      setError(t("conversation.in_progress_ends_soon", "This trip is about to end. Changes can't be proposed in the final 30 minutes."));
      return;
    }
    // Task #2583 — the proposed drop-off must be in the future for an
    // in-progress trip. The server enforces this too, but catching it
    // here gives the user an immediate, actionable error.
    // Task #2974 — same rule when extending a fully-past same-day trip:
    // the renter can propose a new drop-off but it can't be in the past.
    // Selected wall clocks are not elapsed instants. The API validates the
    // proposed return in the booking's saved zone, including gaps and folds.
    setSubmitting(true);
    setError("");
    try {
      // Task #2583: on in-progress trips force the locked fields back
      // to the original trip values regardless of what the editor
      // produced. The server enforces these constraints too, but we
      // never want the request to bounce just because something
      // slipped through the UI lock.
      // Task #2974 — the lock fires the moment the original pickup has
      // passed (`hasStarted`), not just while the trip is live. Once
      // the start instant is past, the start date/time, pickup
      // location and (renter-side) fees can't be moved retroactively
      // even on a same-day booking whose drop-off has also passed.
      const effDateFrom = hasStarted && originalStartDay ? originalStartDay : dateFrom;
      const effPickupHHMM = hasStarted ? originalPickupHHMM : pickupTime;
      const effIsDelivery = hasStarted ? (trip.is_delivery ?? false) : isDelivery;
      const effDeliveryDistance = hasStarted
        ? (trip.delivery_distance ?? null)
        : (isDelivery && !airportCode ? deliveryDistance : null);
      const effAirportCode = hasStarted ? (trip.airport_code ?? null) : (isDelivery ? airportCode : null);
      const effDeliveryAddress = hasStarted
        ? (trip.delivery_address ?? null)
        : (isDelivery && !airportCode ? deliveryAddress : null);
      const effPickupLat = effIsDelivery
        ? (hasStarted
          ? (trip.pickup_latitude ?? null)
          : (effAirportCode ? null : pickupLat))
        : null;
      const effPickupLng = effIsDelivery
        ? (hasStarted
          ? (trip.pickup_longitude ?? null)
          : (effAirportCode ? null : pickupLng))
        : null;
      const result = await bookingsApi.createTripAdjustment(token, trip.id, {
        pickup_proof: quote?.pickupProof,
        date_from: effDateFrom,
        date_to: dateTo,
        // Renters never propose fee overrides — they can only change
        // dates / location, and the recalculated quote is what the
        // owner reviews. Only the owner-side modal sends fees. On
        // in-progress trips fee overrides are blocked entirely.
        rental_fee: !hasStarted && isOwner && rentalFee ? Number(rentalFee) : undefined,
        delivery_fee: !hasStarted && isOwner && isDelivery && deliveryFee ? Number(deliveryFee) : undefined,
        message: message || undefined,
        counter_of: counterOfId ?? undefined,
        pickup_at: composeAdjIso(effDateFrom, effPickupHHMM),
        dropoff_at: composeAdjIso(dateTo, dropoffTime),
        is_delivery: effIsDelivery,
        delivery_distance: effIsDelivery ? effDeliveryDistance : null,
        airport_code: effIsDelivery ? effAirportCode : null,
        delivery_address: effIsDelivery ? effDeliveryAddress : null,
        pickup_latitude: effPickupLat,
        pickup_longitude: effPickupLng,
      });
      // Close the modal immediately on a successful POST. The parent's
      // onSuccess refresh (fetchConversation + fetchMessages + dashboard
      // invalidations) can be slow, and previously we awaited it while
      // the modal was still mounted with `submitting=true` — that made
      // the Send button stay disabled with its spinner, which renters
      // reported as the page "freezing" after clicking Send Request.
      // The parent ConversationPage stays mounted, so its setConv /
      // setMessages calls inside onSuccess still update the underlying
      // page once the refetches resolve.
      setSubmitting(false);
      // Task #7654 — the proposal is persisted server-side; drop the local
      // draft so reopening the modal starts clean from the trip again.
      clearChangeDraft();
      onClose();
      if (onSuccess) {
        void Promise.resolve(onSuccess(result)).catch(() => {
          // Best-effort refresh — the proposal is persisted and the
          // modal has already closed. The parent already swallows its
          // own refresh errors; this catch just prevents an unhandled
          // rejection from the fire-and-forget call.
        });
      }
      return;
    } catch (err) {
      setError(err instanceof Error ? err.message : t("conversation.failed_submit_proposal"));
    } finally {
      setSubmitting(false);
    }
  }

  const sendDisabled = submitting
    || !currentQuote
    || !dateFrom
    || !dateTo
    || quoteLoading
    || !!quoteError
    || !quote
    || (isOwner && rentalFeeBelowFloor);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        // Task #2657 — only treat a click on the backdrop itself as a
        // dismissal. React's synthetic events bubble through the
        // component tree (not the DOM tree), so clicks inside any nested
        // Radix dialog portalled to `<body>` would otherwise bubble up
        // here and wipe the user's in-progress quote.
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="owner-suggest-change-modal"
    >
      <input type="hidden" data-testid="owner-adj-date-from" value={dateFrom} readOnly />
      <input type="hidden" data-testid="owner-adj-date-to" value={dateTo} readOnly />
      <div
        className="relative flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="request-change-modal-title"
      >
        <div className="flex items-center justify-between bg-gradient-to-r from-brand to-brand-mid px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20">
              <PenLine className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 id="request-change-modal-title" className="text-lg font-sans font-bold text-white">
                {counterOfId != null
                  ? t("conversation.counter_proposal")
                  : isOwner ? t("conversation.build_quote") : t("conversation.propose_changes_modal")}
              </h2>
              <p className="text-xs text-white/70">
                {counterOfId != null
                  ? t("conversation.replying_to_proposal", { id: counterOfId })
                  : t("conversation.compare_original_proposed", "Review the original trip and your proposed changes side by side.")}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="cursor-pointer rounded-full p-1.5 text-white/80 transition hover:bg-white/20 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6">
          {isInProgress && (
            <div
              className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800"
              data-testid="rcm-in-progress-notice"
              role="note"
            >
              {inProgressEndsSoon
                ? t("conversation.in_progress_ends_soon", "This trip is about to end. Changes can't be proposed in the final 30 minutes.")
                : t("conversation.in_progress_end_only", "This trip is in progress. Only the drop-off date and time can be changed.")}
            </div>
          )}
          {/* Task #2974 — for trips whose pickup has passed but whose
              drop-off is also already in the past (typical of a same-day
              booking the renter is trying to extend after the fact),
              surface the same start-locked guidance without claiming the
              trip is still "in progress". */}
          {hasStarted && !isInProgress && (
            <div
              className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800"
              data-testid="rcm-start-passed-notice"
              role="note"
            >
              {t(
                "conversation.start_passed_end_only",
                "The pickup time has passed. Only the drop-off date and time can be changed.",
              )}
            </div>
          )}
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {/* Original column */}
            <div
              className="rounded-xl border border-border bg-muted/30 p-4"
              data-testid="rcm-original-column"
            >
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-brand">{t("conversation.original", "Original")}</h3>
                {/* Task #2903 — the Original column is always the
                    committed trip baseline (matching the
                    Reservation panel), never a pending proposal.
                    The badge always reads "Current trip" so the
                    user understands exactly what they're
                    comparing the Proposed column against. */}
                <span
                  className="rounded-full bg-border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-[#5a6b5a]"
                  data-testid="rcm-original-badge"
                >
                  {t("conversation.current_trip", "Current trip")}
                </span>
              </div>
              <div className="space-y-4">
                <ColumnSection
                  title={t("conversation.trip_dates")}
                  primary={<span data-testid="rcm-orig-dates">{origDatesSummary}</span>}
                />
                <div className="h-px bg-border" />
                <ColumnSection
                  title={t("checkout.pickup_dropoff", "Pick-up & drop-off")}
                  primary={<div data-testid="rcm-orig-location">{renderLocationSummary(origIsDelivery, origAddress, origAirportCode, origDeliveryDistance)}</div>}
                />
                <div className="h-px bg-border" />
                <div data-testid="rcm-orig-price">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-[#5a6b5a]">{t("conversation.price", "Price")}</p>
                  <div className="mt-2">
                    {originalSummary ? renderBreakdownRows(originalSummary) : (
                      <p className="text-xs text-[#5a6b5a]" data-testid="rcm-original-unavailable">
                        {baselineLoading ? t("conversation.calculating_price", "Calculating price…") : "—"}
                      </p>
                    )}
                    {isOwner && Number(trip.host_insurance_fee ?? 0) > 0 && (
                      <div className="flex items-baseline justify-between py-1.5 text-xs" data-testid="rcm-orig-host-insurance">
                        <span className="text-[#5a6b5a]">
                          {t("money_breakdown.host_insurance_deduction", "Host insurance")}
                          {trip.host_insurance_policy_snapshot?.package_name
                            ? ` · ${trip.host_insurance_policy_snapshot.package_name}`
                            : ""}
                        </span>
                        <span className="font-medium tabular-nums text-brand">
                          −{formatMoneyWithCurrency(Number(trip.host_insurance_fee), currency)}
                        </span>
                      </div>
                    )}
                    {isOwner && trip.host_insurance_policy_snapshot && (
                      <p className="text-[10px] text-[#8a9a8a]" data-testid="rcm-orig-host-insurance-policy">
                        {trip.host_insurance_policy_snapshot.state_code} · {t("money_breakdown.insured_value", "Insured value")}{" "}
                        {formatMoneyWithCurrency(trip.host_insurance_policy_snapshot.insured_value, currency)}
                      </p>
                    )}
                    <div className="mt-2 flex items-center justify-between border-t border-border pt-2">
                      <span className="font-semibold text-brand">{isOwner ? t("pricing.host_basis_total", "Booking price (host basis)") : t("conversation.total", "Total")}</span>
                      <span className="font-semibold text-brand tabular-nums" data-testid="rcm-orig-total">
                        {originalSummary ? formatMoneyWithCurrency(origTotal, currency) : "—"}
                      </span>
                    </div>
                    {isOwner && <p className="mt-1 text-xs text-muted-foreground">{t("pricing.host_basis_note", "Prices before guest offers; not the guest's payment amount.")}</p>}
                    {/* Task #2641 — host payout for the Original column.
                        Sources from the trip's stored `owner_earn` (or
                        the pending proposal's suggested payout when one
                        is in play) so the host can compare current vs.
                        proposed take-home at a glance. Hidden for renters. */}
                    {isOwner && (
                      <div
                        className="mt-1 flex items-baseline justify-between rounded-xl bg-white px-3 py-2.5 text-sm"
                        data-testid="rcm-orig-owner-earn"
                      >
                        <span className="font-semibold text-brand">{t("money_breakdown.your_earnings")}</span>
                        <span className="font-semibold text-brand tabular-nums">{originalOwnerEarn != null ? formatMoneyWithCurrency(originalOwnerEarn, currency) : "—"}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Proposed column */}
            <div
              className="rounded-xl border border-brand/30 bg-white p-4 ring-1 ring-brand/10"
              data-testid="rcm-proposed-column"
            >
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-brand">{t("conversation.proposed", "Proposed")}</h3>
                {quoteLoading && (
                  <span className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-[#5a6b5a]" data-testid="rcm-quote-loading">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {t("common.updating", "Updating")}
                  </span>
                )}
              </div>
              <div className="space-y-4">
                <ColumnSection
                  title={t("conversation.trip_dates")}
                  primary={<span data-testid="rcm-dates-summary">{proposedDatesSummary}</span>}
                  trigger={
                    // Task #2940 follow-up — use the same CheckoutDatesEditor
                    // for in-progress trips as for normal trips, with
                    // `lockStart` so the start pill and pickup TimePicker
                    // are immovable. The calendar still shows both months
                    // and the highlighted range, so the in-progress editor
                    // is visually identical to the normal one — just with
                    // the start half disabled.
                    <CheckoutDatesEditor
                      trigger={
                        <button type="button" className="text-xs font-medium text-brand underline" data-testid="rcm-dates-trigger">
                          {t("common.change")}
                        </button>
                      }
                      rentalType={tripRentalType}
                      dateFrom={dateFrom}
                      dateTo={dateTo}
                      pickupTime={pickup12}
                      dropoffTime={dropoff12}
                      hours={undefined}
                      availability={availability}
                      loadingAvailability={loadingAvailability}
                      autoApplyOnDismiss={isOwner && !hasStarted}
                      lockStart={hasStarted}
                      onApply={(next) => {
                        setError("");
                        setProposalEdited(true);
                        // In lockStart mode the editor forwards the locked
                        // start verbatim, but re-assert here so a buggy
                        // future change to the editor can't slip a moved
                        // start through.
                        // Task #2974 — gate on `hasStarted` so the same
                        // re-assert protects fully-past same-day trips.
                        setDateFrom(hasStarted ? dateFrom : next.dateFrom);
                        setDateTo(next.dateTo);
                        setPickupTime(hasStarted ? pickupTime : time12ToHhmm(next.pickupTime));
                        setDropoffTime(time12ToHhmm(next.dropoffTime));
                      }}
                    />
                  }
                />
                <div className="h-px bg-border" />
                {/* Renters can edit a location without gaining fee
                    override inputs. Self-drive mode changes are governed
                    directionally by the current listing capabilities. */}
                <ColumnSection
                  title={t("checkout.pickup_dropoff", "Pick-up & drop-off")}
                  primary={
                    <div data-testid="rcm-location-summary">
                      {renderLocationSummary(isDelivery, deliveryAddress, airportCode, deliveryDistance)}
                    </div>
                  }
                  // Task #2420 — the row-level helper note is gone; the
                  // mode-switch guidance now lives inside CheckoutLocationEditor
                  // (rendered only when lockMode=true and only at the moment
                  // the renter actually opens the editor) so the messages-page
                  // row stays clean and the renter sees the constraint exactly
                  // when it's actionable.
                  trigger={
                    hasStarted ? (
                      // Task #2583 — pickup location is locked once the
                      // trip is underway. We surface a read-only summary
                      // (already rendered via `primary`) and omit the
                      // change trigger entirely so neither side can edit.
                      // Task #2974 — apply the same lock to trips whose
                      // pickup has passed even if the drop-off is also
                      // past (extending a same-day booking). The pickup
                      // location is historical fact at that point.
                      <span className="text-xs text-[#8a9a8a]" data-testid="rcm-location-locked">
                        {t("conversation.locked_in_progress", "Locked")}
                      </span>
                    ) : (
                    <>
                    {deliveryOptionsStatus === "loading" ? (
                      <span role="status" className="mr-2 text-xs text-muted-foreground">
                        {t("common.loading", "Loading…")}
                      </span>
                    ) : null}
                    {deliveryOptionsStatus === "error" ? (
                      <span className="mr-2 text-xs text-destructive" role="alert">
                        {t("checkout.edit_dates_no_availability", "Availability data unavailable. Please try again.")}
                        {" "}
                        <button
                          type="button"
                          className="underline"
                          data-testid="rcm-delivery-options-retry"
                          onClick={fetchBookedCarDeliveryOptions}
                        >
                          {t("common.retry")}
                        </button>
                      </span>
                    ) : null}
                    <CheckoutLocationEditor
                      audience={isOwner ? "host" : "renter"}
                      trigger={
                        <button type="button" className="text-xs font-medium text-brand underline" data-testid="rcm-location-trigger">
                          {t("common.change")}
                        </button>
                      }
                      pickupCity={car?.city || ""}
                      pickupState={car?.state || ""}
                      carLatitude={car?.latitude ?? null}
                      carLongitude={car?.longitude ?? null}
                      countryCode={car?.country_code ?? null}
                      currency={currency}
                      deliveryMaxRadius={carDeliveryRadius}
                      deliveryAirports={carDeliveryAirports}
                      currentDistance={isDelivery ? deliveryDistance : null}
                      currentAirportCode={isDelivery ? airportCode : null}
                      currentAddress={isDelivery ? deliveryAddress : null}
                      currentIsDelivery={isDelivery}
                      currentLatitude={isDelivery ? pickupLat : null}
                      currentLongitude={isDelivery ? pickupLng : null}
                      deliveryEnabled={locationPolicy.deliveryEnabled}
                      lockMode={locationPolicy.lockMode}
                      allowPickupMode={locationPolicy.allowPickupMode}
                      allowDeliveryMode={locationPolicy.allowDeliveryMode}
                      modeEligibilityNote={
                        tripRentalType === "driving" && !isDelivery
                          ? deliveryOptionsStatus === "loading"
                            ? t("common.loading", "Loading…")
                            : deliveryOptionsStatus === "error"
                              ? t("checkout.edit_dates_no_availability", "Availability data unavailable. Please try again.")
                              : deliveryOptionsStatus === "ready" && !locationPolicy.allowDeliveryMode
                                ? t(
                                    "checkout.self_drive_delivery_unavailable_for_change",
                                    "Delivery can't be added to this self-drive trip because it's not enabled for this listing.",
                                  )
                                : undefined
                          : undefined
                      }
                      // Task #2664 — event hires require delivery. The
                      // listing wizard already enforces this at booking
                      // time; the host quote modal now matches by
                      // forcing the editor into delivery mode and
                      // disabling the "Pick up myself" tile so the host
                      // can't propose a self-pickup event hire.
                      requireDelivery={locationPolicy.requireDelivery}
                      onApply={(next) => {
                        // Distance is allowed to round to zero. Address text
                        // alone is not a delivery signal; the editor's
                        // explicit mode is represented by an airport or a
                        // non-null distance, including zero.
                        const nextIsDelivery = !!next.deliveryAirportCode || next.deliveryDistance != null;
                        setProposalEdited(true);
                        setIsDelivery(nextIsDelivery);
                        setDeliveryDistance(nextIsDelivery ? next.deliveryDistance : null);
                        setAirportCode(nextIsDelivery ? next.deliveryAirportCode : null);
                        setDeliveryAddress(nextIsDelivery ? next.deliveryAddress : null);
                        setPickupLat(nextIsDelivery ? next.deliveryLatitude : null);
                        setPickupLng(nextIsDelivery ? next.deliveryLongitude : null);
                        if (!nextIsDelivery) {
                          setDeliveryFee("");
                          setDeliveryFeeTouched(false);
                          setDeliveryFeeOwnerEdited(false);
                          setDeliveryFeeSeedSource(null);
                        }
                      }}
                    />
                    </>
                    )
                  }
                />
                {isOwner && !hasStarted && (
                  <>
                    <div className="h-px bg-border" />
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-[#5a6b5a]">{t("conversation.fees")}</p>
                      <div className="mt-2 grid grid-cols-2 gap-3">
                        <div>
                          <label className="mb-1 block text-xs text-[#5a6b5a]">{t("conversation.fee_rental")}</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#5a6b5a]">{sym}</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={rentalFee}
                              onChange={(e) => { setProposalEdited(true); setRentalFeeTouched(true); setRentalFeeOwnerEdited(true); setRentalFee(e.target.value); }}
                              placeholder={quote?.rentalFee != null ? Number(quote.rentalFee).toFixed(2) : t("conversation.fee_auto")}
                              data-testid="owner-adj-rental-fee"
                              className={`w-full rounded-lg border border-border bg-muted/30 py-2 ${currencySymbolPadding(sym)} pr-3 text-base md:text-sm text-brand placeholder:text-[#5a6b5a]/50 focus:border-brand/30 focus:outline-none focus:ring-1 focus:ring-brand/10`}
                            />
                          </div>
                          {(quote || rentalFloor.multiplier > 0) && (
                            <p
                              className={`mt-1 text-[11px] ${rentalFeeBelowFloor ? "text-red-600" : "text-[#8a9a8a]"}`}
                              data-testid="rcm-min-rental-hint"
                            >
                              {/* Task #2664 — anchor the reference number
                                  to the booked rental (Original column's
                                  reconciled Rental line) until the host
                                  edits a quote-affecting input. After the
                                  first edit only the Minimum + based-on-N
                                  suffix renders; the prior "Calculated at
                                  current rate: £X" line was confusing
                                  because it surfaced a fresh re-quote
                                  number while the input still held the
                                  booked amount. */}
                              {!proposalEdited && rentalFeeHintOriginal != null && (
                                <>
                                  <span data-testid="rcm-rental-hint-original">
                                    {t("conversation.original_rental_fee", "Original")}: {formatMoneyWithCurrency(rentalFeeHintOriginal, currency)}
                                  </span>
                                  {" · "}
                                </>
                              )}
                              {rentalFloor.available
                                ? t(
                                  "conversation.min_rental_fee_hint",
                                  "Minimum {{min}}",
                                  { min: formatMoneyWithCurrency(rentalFloor.amount, currency) },
                                )
                                : t(
                                  "conversation.min_rental_fee_unavailable",
                                  "Minimum unavailable",
                                )}
                              {rentalFloor.multiplier > 0 && (
                                <>
                                  {" · "}
                                  {rentalFloor.unit === "hour"
                                    ? t(
                                      "conversation.based_on_hours",
                                      "based on {{count}} hours",
                                      { count: rentalFloor.multiplier },
                                    )
                                    : t(
                                      "conversation.based_on_days",
                                      "based on {{count}} days",
                                      { count: rentalFloor.multiplier },
                                    )}
                                </>
                              )}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="mb-1 block text-xs text-[#5a6b5a]">{t("conversation.fee_delivery")}</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#5a6b5a]">{sym}</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={deliveryFee}
                              disabled={!isDelivery}
                              aria-describedby={!isDelivery ? "delivery-fee-pickup-help" : undefined}
                              onChange={(e) => { if (!isDelivery) return; setProposalEdited(true); setDeliveryFeeTouched(true); setDeliveryFeeOwnerEdited(true); setDeliveryFee(e.target.value); }}
                              placeholder={quote?.deliveryFee && quote.deliveryFee > 0 ? Number(quote.deliveryFee).toFixed(2) : "0.00"}
                              data-testid="owner-adj-delivery-fee"
                              className={`w-full rounded-lg border border-border bg-muted/30 py-2 ${currencySymbolPadding(sym)} pr-3 text-base md:text-sm text-brand placeholder:text-[#5a6b5a]/50 focus:border-brand/30 focus:outline-none focus:ring-1 focus:ring-brand/10`}
                            />
                            {!isDelivery && <p id="delivery-fee-pickup-help" className="mt-1 text-xs text-muted-foreground">{t("pricing.delivery_first", "Select delivery first to add a delivery fee.")}</p>}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                <div className="h-px bg-border" />
                <div data-testid="rcm-proposed-price">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-[#5a6b5a]">{t("conversation.price", "Price")}</p>
                  <div className="mt-2">
                    {quoteError ? (
                      <Alert variant="destructive" data-testid="rcm-quote-error">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription className="text-xs" data-testid="rcm-quote-error-message">
                          {/* Task #2910 — surface the real reason the
                              server refused to price this change instead
                              of the previous one-size-fits-all "adjust
                              the dates" copy. For the documented codes
                              we re-use the same translation keys the
                              CheckoutPage / BookingWidget already use
                              for delivery-radius failures so the copy
                              stays consistent across surfaces. Every
                              other documented error (`/bookings/quote`
                              returns a human-readable `error` for
                              past-date, end-before-start, invalid
                              airport, missing pricing, etc.) renders
                              verbatim. */}
                          {quoteError.code === "DELIVERY_BEYOND_MAX_RADIUS"
                            ? t(
                                "booking.delivery_too_far_radius",
                                "This location is outside the car's delivery range (maximum {{max}} {{unit}}).",
                                {
                                  max: quoteError.maxRadius ?? carDeliveryRadius ?? "",
                                  unit: getDistanceUnitPlural(car?.country_code ?? null),
                                },
                              )
                            : quoteError.message
                              || t("conversation.quote_unavailable", "We couldn't price these changes. Try again in a moment.")}
                        </AlertDescription>
                      </Alert>
                    ) : !effectiveProposedSummary ? (
                      <div className="flex items-center gap-2 py-3 text-xs text-[#8a9a8a]">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        {t("conversation.calculating_price", "Calculating price…")}
                      </div>
                    ) : (
                      <>
                        {renderBreakdownRows(effectiveProposedSummary, { feeSources: { rental: proposedRentalFeeSource, delivery: proposedDeliveryFeeSource } })}
                        {isOwner && Number(quote?.hostInsuranceFee ?? 0) > 0 && (
                          <div className="flex items-baseline justify-between py-1.5 text-xs" data-testid="rcm-proposed-host-insurance">
                            <span className="text-[#5a6b5a]">
                              {t("money_breakdown.host_insurance_deduction", "Host insurance")}
                              {quote?.hostInsurancePolicy?.package_name
                                ? ` · ${quote.hostInsurancePolicy.package_name}`
                                : ""}
                            </span>
                            <span className="font-medium tabular-nums text-brand">
                              −{formatMoneyWithCurrency(Number(quote?.hostInsuranceFee), currency)}
                            </span>
                          </div>
                        )}
                        {isOwner && quote?.hostInsurancePolicy && (
                          <p className="text-[10px] text-[#8a9a8a]" data-testid="rcm-proposed-host-insurance-policy">
                            {quote.hostInsurancePolicy.state_code} · {t("money_breakdown.insured_value", "Insured value")}{" "}
                            {formatMoneyWithCurrency(quote.hostInsurancePolicy.insured_value, currency)}
                          </p>
                        )}
                        <div className="mt-2 flex items-center justify-between border-t border-border pt-2">
                          <span className="font-semibold text-brand">{isOwner ? t("pricing.host_basis_total", "Booking price (host basis)") : t("conversation.total", "Total")}</span>
                          <span className="font-semibold text-brand tabular-nums" data-testid="rcm-proposed-total">
                            {formatMoneyWithCurrency(proposedTotal ?? 0, currency)}
                          </span>
                        </div>
                        {/* Task #2641 — host payout for the Proposed
                            column. Same i18n key + styling as the
                            checkout MoneyBreakdown's owner-earn row so
                            the host sees a familiar "Your earnings"
                            line that updates live with fee/date/location
                            edits. Hidden for renters. */}
                        {isOwner && proposedOwnerEarn != null && (
                          <div
                            className="mt-1 flex items-baseline justify-between rounded-xl bg-muted/30 px-3 py-2.5 text-sm"
                            data-testid="rcm-proposed-owner-earn"
                          >
                            <span className="font-semibold text-brand">{t("money_breakdown.your_earnings")}</span>
                            <span className="font-semibold text-brand tabular-nums">{formatMoneyWithCurrency(proposedOwnerEarn, currency)}</span>
                          </div>
                        )}
                        {/* Task #3531 — partial-pay renter: split the
                            single "+X vs original" preview into the card
                            charge (platform portion) and the owner-direct
                            amount paid at pickup. Hosts + full-pay renters
                            keep the legacy single-line delta below. */}
                        {renterPartialDelta != null ? (
                          (() => {
                            const cardAmt = renterPartialDelta.card.amount;
                            const pickup = renterPartialDelta.ownerAtPickup;
                            const pickupAmt = pickup?.amount ?? 0;
                            const noChange = Math.abs(cardAmt) < 0.01 && Math.abs(pickupAmt) < 0.01;
                            if (noChange) {
                              return (
                                <p className="mt-1 text-right text-xs text-[#8a9a8a]" data-testid="rcm-no-price-change">
                                  {t("conversation.no_price_change", "No price change")}
                                </p>
                              );
                            }
                            return (
                              <div className="mt-1 space-y-0.5 text-right text-xs">
                                {Math.abs(cardAmt) >= 0.01 && (
                                  <p
                                    className={cardAmt < 0 ? "text-emerald-700" : "text-amber-700"}
                                    data-testid="rcm-card-delta-preview"
                                  >
                                    {cardAmt > 0 ? "+" : "−"}{formatMoneyWithCurrency(Math.abs(cardAmt), currency)}{" "}
                                    {t("conversation.charged_now_vs_original", {
                                      defaultValue: "charged to card vs. original",
                                    })}
                                  </p>
                                )}
                                {pickup != null && Math.abs(pickupAmt) >= 0.01 && (
                                  <p className="text-[#5a6b5a]" data-testid="rcm-owner-at-pickup-delta-preview">
                                    {pickupAmt > 0 ? "+" : "−"}{formatMoneyWithCurrency(Math.abs(pickupAmt), currency)}{" "}
                                    {t("conversation.paid_at_pickup_vs_original", {
                                      defaultValue: "paid to host at pickup vs. original",
                                    })}
                                  </p>
                                )}
                              </div>
                            );
                          })()
                        ) : (
                          <>
                            {viewerDelta != null && Math.abs(viewerDelta) >= 0.01 && (
                              <p
                                className={`mt-1 text-right text-xs ${viewerDeltaIsGood ? "text-emerald-700" : "text-amber-700"}`}
                                data-testid="owner-adj-delta-preview"
                              >
                                {viewerDelta > 0 ? "+" : "−"}{formatMoneyWithCurrency(Math.abs(viewerDelta), currency)}{" "}
                                {viewerDelta > 0
                                  ? t("conversation.vs_original_more", "vs. original")
                                  : t("conversation.vs_original_less", "vs. original")}
                              </p>
                            )}
                            {viewerDelta != null && Math.abs(viewerDelta) < 0.01 && (
                              <p className="mt-1 text-right text-xs text-[#8a9a8a]" data-testid="rcm-no-price-change">
                                {t("conversation.no_price_change", "No price change")}
                              </p>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-5">
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#5a6b5a]">{t("conversation.message_optional")}</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="adj-message"
              rows={2}
              placeholder={t("conversation.add_proposal_note")}
              className="w-full resize-none rounded-lg border border-border bg-muted/30 px-3 py-2 text-base md:text-sm text-brand placeholder:text-[#5a6b5a]/50 focus:border-brand/30 focus:outline-none focus:ring-1 focus:ring-brand/10"
            />
          </div>

          {error && (
            <Alert variant="destructive" className="mt-4">
              <AlertDescription className="text-xs">{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-border bg-muted/30 px-6 py-4">
          <button
            onClick={onClose}
            className="cursor-pointer rounded-lg border border-border px-5 py-2.5 text-sm font-medium text-[#5a6b5a] transition hover:border-brand/30 hover:text-brand"
          >
            {t("conversation.cancel")}
          </button>
          <button
            onClick={handleSubmit}
            disabled={sendDisabled}
            data-testid="owner-adj-submit"
            className="cursor-pointer flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-brand-hover disabled:opacity-50"
          >
            {submitting ? <Spinner size="sm" className="text-white" /> : <Send className="h-4 w-4" />}
            {t("conversation.send_proposal")}
          </button>
        </div>
      </div>

    </div>
  );
}
